// src/pages/TeacherDashboard.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FaUserCircle,
  FaCalendarAlt,
  FaFileAlt,
  FaHome,
  FaUpload,
  FaClipboardList,
  FaCheckCircle,
  FaTimesCircle,
  FaGraduationCap,
  FaSlidersH,
  FaChartLine,
  FaClock,
  FaStar,
  FaExclamationTriangle,
  FaMoneyBillWave,
  FaVideo,
  FaPlayCircle,
  FaStopCircle,
  FaExternalLinkAlt,
  FaCopy,
  FaBroadcastTower,
  FaSync,
  FaArrowUp,
  FaArrowDown,
  FaBolt,
  FaSpinner,
} from "react-icons/fa";
import StudentProfileModal from "../components/StudentProfileModal";
import ReportCardModal from "../components/ReportCardModal";
import WebRTCClassRoom from "../components/WebRTCClassRoom";
import ProctoredAssessment from "../components/ProctoredAssessment";
import AdminModalShell from "../components/AdminModalShell";
import { toast } from "react-toastify";
import api from "../api/httpClient";
import { API_PATHS } from "../api/apiPaths";
import { useTeacherProfile } from "../context/TeacherProfileContext";
import { useTenant } from "../context/TenantContext";
import { getTeacherPayrollPayments } from "../utils/teacherPayrollLedger";
import { resolveApiErrorMessage, getApiErrorCode } from "../utils/errorMessages";

/**
 * TeacherDashboard with attendance panel
 */

const PERFORMANCE_SUBJECTS = ["Mathematics", "Science", "English"];
const SAMPLE_INSIGHT_STUDENTS = [
  {
    id: 901,
    firstName: "Aarav",
    lastName: "Sharma",
    rollNumber: "CI-041",
    insightScores: [94, 91, 88],
    insightAttendance: 96,
    insightAbsentToday: false,
    insightTrendDelta: 8,
  },
  {
    id: 902,
    firstName: "Zoya",
    lastName: "Khan",
    rollNumber: "CI-017",
    insightScores: [63, 59, 61],
    insightAttendance: 71,
    insightAbsentToday: false,
    insightTrendDelta: -4,
  },
  {
    id: 903,
    firstName: "Kabir",
    lastName: "Mehta",
    rollNumber: "CI-026",
    insightScores: [58, 52, 55],
    insightAttendance: 68,
    insightAbsentToday: true,
    insightTrendDelta: -9,
  },
  {
    id: 904,
    firstName: "Ananya",
    lastName: "Iyer",
    rollNumber: "CI-032",
    insightScores: [81, 78, 84],
    insightAttendance: 89,
    insightAbsentToday: false,
    insightTrendDelta: 5,
  },
  {
    id: 905,
    firstName: "Vihaan",
    lastName: "Rao",
    rollNumber: "CI-038",
    insightScores: [72, 75, 69],
    insightAttendance: 77,
    insightAbsentToday: false,
    insightTrendDelta: 2,
  },
];
const CLASS_COLLECTION_KEYS = ["classes", "classrooms", "classList", "teacherClasses", "assignedClasses"];
const STUDENT_COLLECTION_KEYS = ["students", "learners"];
const SCHEDULE_COLLECTION_KEYS = ["schedule", "slots", "timetable"];
const ATTENDANCE_COLLECTION_KEYS = ["attendance", "records"];
const ONLINE_CLASS_COLLECTION_KEYS = ["rooms", "sessions", "onlineClasses", "classrooms", "liveClasses"];
const COMMON_COLLECTION_KEYS = ["data", "items", "results", "records", "content", "rows"];

const ATTENDANCE_STATUS_VALUES = ["PRESENT", "ABSENT", "LATE", "EXCUSED"];
const NOT_MARKED = "NOT_MARKED";

// Students absent from the today's-attendance response are NOT_MARKED, never ABSENT.
const normalizeAttendanceStatus = (value) => {
  const normalized = String(value || "").trim().toUpperCase();
  return ATTENDANCE_STATUS_VALUES.includes(normalized) ? normalized : NOT_MARKED;
};

const ATTENDANCE_STATUS_LABELS = {
  PRESENT: "Present",
  ABSENT: "Absent",
  LATE: "Late",
  EXCUSED: "Excused",
  [NOT_MARKED]: "Not Marked",
};

const ATTENDANCE_STATUS_BADGE_CLASSES = {
  PRESENT: "bg-emerald-600 text-white shadow-sm shadow-emerald-200",
  ABSENT: "bg-rose-600 text-white shadow-sm shadow-rose-200",
  LATE: "bg-amber-500 text-white shadow-sm shadow-amber-200",
  EXCUSED: "bg-sky-600 text-white shadow-sm shadow-sky-200",
  [NOT_MARKED]: "bg-slate-100 text-slate-600",
};

const CORRECTION_STATUS_VALUES = ["PENDING", "APPROVED", "COMPLETED"];

const normalizeCorrectionStatus = (record = {}) => {
  const raw = String(
    record.correctionStatus
    ?? record.correctionRequestStatus
    ?? record.correction?.status
    ?? record.correctionState
    ?? ""
  ).trim().toUpperCase();
  return CORRECTION_STATUS_VALUES.includes(raw) ? raw : null;
};

const unwrapResponsePayload = (response) => response?.data?.data ?? response?.data ?? null;

const resolveArrayFromCandidate = (candidate, preferredKeys = []) => {
  if (Array.isArray(candidate)) return candidate;
  if (!candidate || typeof candidate !== "object") return null;

  const keys = [...preferredKeys, ...COMMON_COLLECTION_KEYS];

  for (const key of keys) {
    if (Array.isArray(candidate[key])) return candidate[key];
  }

  for (const key of keys) {
    const nested = candidate[key];
    if (!nested || typeof nested !== "object") continue;
    for (const nestedKey of keys) {
      if (Array.isArray(nested[nestedKey])) return nested[nestedKey];
    }
  }

  return null;
};

const extractArrayFromResponse = (response, preferredKeys = []) => {
  const payload = unwrapResponsePayload(response);
  const payloadArray = resolveArrayFromCandidate(payload, preferredKeys);
  if (payloadArray) return payloadArray;
  return resolveArrayFromCandidate(response?.data, preferredKeys) || [];
};

const normalizeClassRecord = (record = {}, index = 0) => ({
  ...record,
  id: record.id ?? record.classId ?? record._id ?? `class-${index + 1}`,
  name: record.name || record.className || record.title || `Class ${index + 1}`,
});

const normalizeScheduleRecord = (record = {}, index = 0) => {
  const start = record.startTime || record.startsAt || record.start || "";
  const end = record.endTime || record.endsAt || record.end || "";
  return {
    ...record,
    id: record.id ?? record.scheduleId ?? record.slotId ?? `schedule-${index + 1}`,
    title: record.title || record.subjectName || record.subject?.name || record.subject || "Class",
    time: record.time || record.timeRange || [start, end].filter(Boolean).join(" - "),
  };
};

const hasSelectedClassId = (value) =>
  value !== null && value !== undefined && String(value).trim() !== "";

const normalizeStudentRecord = (record = {}, index = 0) => {
  const id = record.id ?? record.studentId ?? record._id ?? record.userId ?? `student-${index + 1}`;
  const displayName = record.name || record.fullName || record.studentName || "";
  const nameParts = displayName.trim().split(/\s+/).filter(Boolean);
  const firstName = record.firstName || record.givenName || nameParts[0] || "Student";
  const lastName = record.lastName || record.surname || nameParts.slice(1).join(" ");
  const rollNumber =
    record.rollNumber ||
    record.rollNo ||
    record.admissionNo ||
    record.admissionNumber ||
    (id ? String(id).slice(0, 6).toUpperCase() : "N/A");

  return {
    ...record,
    id,
    name: displayName || `${firstName} ${lastName}`.trim(),
    firstName,
    lastName,
    rollNumber,
  };
};

const normalizeClassGroup = (group = {}) => ({
  className: String(group.className ?? group.class ?? group.name ?? "").trim(),
  students: Array.isArray(group.students) ? group.students.map((s, i) => normalizeStudentRecord(s, i)) : [],
});

// Used to match class labels across the different class endpoints, which use
// inconsistent formats ("10" vs "Class 10" vs the teacher class's own name).
const normalizeClassKey = (value = "") =>
  String(value).trim().toLowerCase().replace(/^class\s*/, "");

// Dropdown returns display labels like "Class 10"; rawName ("10") is what the
// rest of the attendance flow (classId, className groups) matches against.
const normalizeDropdownClass = (item = {}) => {
  const name = String(item.name ?? item.className ?? "").trim();
  const rawName = name.replace(/^class\s*/i, "").trim() || name;
  return { id: item.id ?? item.classId ?? item._id ?? rawName, name, rawName };
};

const normalizeOnlineClassSession = (session = {}, index = 0) => {
  const attendees = Array.isArray(session.attendees)
    ? session.attendees
    : Array.isArray(session.participants)
      ? session.participants
      : [];

  const scheduledAt = session.scheduledAt || session.startAt || session.startTime || session.createdAt || new Date().toISOString();
  const durationMinutes = Number(session.durationMinutes || session.duration || session.durationInMinutes || 60);
  const scheduledEndAt = session.scheduledEndAt || session.endAt || new Date(
    new Date(scheduledAt).getTime() + durationMinutes * 60 * 1000
  ).toISOString();
  const rawStatus = String(
    session.status || session.roomStatus || session.sessionStatus || "scheduled"
  ).toLowerCase();
  const resolvedStatus = rawStatus === "scheduled"
    && !Number.isNaN(new Date(scheduledEndAt).getTime())
    && new Date(scheduledEndAt).getTime() <= Date.now()
    ? "expired"
    : rawStatus;

  return {
    id: session.id || session.sessionId || `session-${Date.now()}-${index}`,
    title: session.title || session.name || "Online Class Session",
    classId: session.classId || session.class?.id || session.class?.classId || "",
    className: session.className || session.class?.name || session.class?.className || "Class",
    roomId: session.roomCode || session.roomId || session.classCode || session.code || "",
    joinUrl: session.joinUrl || "",
    joinable: typeof session.joinable === "boolean" ? session.joinable : resolvedStatus === "live",
    section: session.section || "",
    startAt: scheduledAt,
    scheduledAt,
    scheduledEndAt,
    startedAt: session.startedAt || "",
    endedAt: session.endedAt || "",
    durationMinutes,
    status: resolvedStatus,
    createdBy: session.createdBy || "",
    attendees,
  };
};

export default function TeacherDashboard() {
  const navigate = useNavigate();
  const rawUserName = localStorage.getItem("currentUserName") || "";
  const { teacherProfile } = useTeacherProfile();
  const { tenantFeatures, tenantFeaturesLoading } = useTenant();
  const displayName = teacherProfile?.name || (rawUserName
    ? rawUserName
      .split("@")[0]
      .replace(/[._-]+/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase())
    : "Teacher");
  const defaultTeacherPrefs = {
    showClassControls: true,
    showSchedule: true,
    showOnlineClasses: true,
    showStudentReports: true,
    showSubmitMarks: true,
    showUploadNotes: true,
    showLeaveRequests: true,
    showHolidayRequests: true,
    showAttendancePanel: true,
    showPerformanceInsights: true,
    showAiRiskRadar: true,
    showAssessments: true,
  };
  const loadTeacherPrefs = () => {
    try {
      const stored = localStorage.getItem("teacherDashboardPrefs");
      if (!stored) return defaultTeacherPrefs;
      const parsed = JSON.parse(stored);
      return { ...defaultTeacherPrefs, ...parsed };
    } catch {
      return defaultTeacherPrefs;
    }
  };
  const [prefs, setPrefs] = useState(loadTeacherPrefs);
  const [draftPrefs, setDraftPrefs] = useState(loadTeacherPrefs);
  const [showCustomize, setShowCustomize] = useState(false);
  const [classes, setClasses] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState(null);
  const [students, setStudents] = useState([]);
  // Class -> students groups from the teacher-dashboard-only grouped-by-class API.
  const [classGroups, setClassGroups] = useState([]);
  // Nice class-select options from the dedicated classes-dropdown API.
  const [classDropdown, setClassDropdown] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [scheduleError, setScheduleError] = useState("");
  const [showProfile, setShowProfile] = useState(false);
  const [activeStudent, setActiveStudent] = useState(null);
  const [showReportCard, setShowReportCard] = useState(false);
  const [loading, setLoading] = useState(false);
  // studentId -> { attendanceId, status, correctionStatus }
  const [attendanceRecords, setAttendanceRecords] = useState({});
  const [markingStudentId, setMarkingStudentId] = useState(null);
  const [markingAll, setMarkingAll] = useState(false);
  const [markAllStatus, setMarkAllStatus] = useState("PRESENT");
  const [applyingCorrectionId, setApplyingCorrectionId] = useState(null);
  const [correctionModal, setCorrectionModal] = useState({
    open: false,
    studentId: null,
    attendanceId: null,
    reason: "",
  });
  const [submittingCorrection, setSubmittingCorrection] = useState(false);
  const [correctionApplyStatus, setCorrectionApplyStatus] = useState({});
  const [usingOfflineData, setUsingOfflineData] = useState(false);
  const [reportStudentId, setReportStudentId] = useState("");
  const [attendanceReport, setAttendanceReport] = useState(null);
  const [activeSection, setActiveSection] = useState("overview");
  const [activeOnlineClassTab, setActiveOnlineClassTab] = useState("schedule");
  const [activeAttendanceTab, setActiveAttendanceTab] = useState("mark");
  const [activeInsightsTab, setActiveInsightsTab] = useState("risk");
  const [activeStudentsTab, setActiveStudentsTab] = useState("reports");
  const [activeResourcesTab, setActiveResourcesTab] = useState("notes");
  const [onlineClassForm, setOnlineClassForm] = useState({
    title: "",
    startAt: "",
    durationMinutes: "60",
    section: "",
    startImmediately: false,
  });
  const [onlineClassSessions, setOnlineClassSessions] = useState([]);
  const [activeWebRtcSessionId, setActiveWebRtcSessionId] = useState("");
  const [creatingOnlineClass, setCreatingOnlineClass] = useState(false);
  const [updatingOnlineClass, setUpdatingOnlineClass] = useState("");

  const [markForm, setMarkForm] = useState({
    studentId: "",
    subject: "Mathematics",
    score: "",
    max: "100",
    remarks: "",
  });
  const [noteForm, setNoteForm] = useState({
    title: "",
    classId: "",
    file: null,
  });
  const [classNotes, setClassNotes] = useState([]);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [holidayRequests, setHolidayRequests] = useState([]);

  const teacherDashboardFeatures = tenantFeatures?.teacherDashboard || tenantFeatures?.teacher_dashboard;
  const scheduleFeatureValue = teacherDashboardFeatures?.tabs?.schedule
    ?? teacherDashboardFeatures?.items?.schedule
    ?? teacherDashboardFeatures?.schedule;
  const scheduleFeatureEnabled = teacherDashboardFeatures?.enabled !== false && scheduleFeatureValue !== false;

  // studentId -> status string, derived for reports/insights that only need the status.
  const attendanceMap = useMemo(() => {
    const map = {};
    Object.entries(attendanceRecords).forEach(([studentId, record]) => {
      map[studentId] = record.status;
    });
    return map;
  }, [attendanceRecords]);

  // Attendance class-select: always uses the real teacher class id (so the
  // students/attendance-today/mark calls work), with a nicer label sourced
  // from the classes-dropdown / grouped-by-class APIs when a match is found.
  const attendanceClassOptions = classes.length
    ? classes.map((cls) => {
      const key = normalizeClassKey(cls.name || cls.id);
      const dropdownMatch = classDropdown.find(
        (item) => normalizeClassKey(item.rawName) === key || normalizeClassKey(item.name) === key
      );
      const groupMatch = classGroups.find((group) => normalizeClassKey(group.className) === key);
      const niceName = dropdownMatch?.name || (groupMatch ? `Class ${groupMatch.className}` : cls.name);
      return { id: cls.id, name: niceName };
    })
    : classDropdown.map((item) => ({ id: item.id, name: item.name }));

  useEffect(() => {
    loadClasses();
    loadStudentsGroupedByClass();
    loadClassesDropdown();
  }, []);

  useEffect(() => {
    if (classes.length || !classDropdown.length) return;
    const recoveredClasses = classDropdown.map((item, index) => normalizeClassRecord({
      id: item.id,
      name: item.name || item.rawName,
    }, index));
    setClasses(recoveredClasses);
    setSelectedClassId((current) => current ?? recoveredClasses[0]?.id ?? null);
  }, [classes.length, classDropdown]);

  useEffect(() => {
    const hasClass = hasSelectedClassId(selectedClassId);

    loadStudents(hasClass ? selectedClassId : null);
    setAttendanceReport(null);

    if (!hasClass) {
      setSchedule([]);
      setAttendanceRecords({});
      return;
    }

    if (scheduleFeatureEnabled) loadSchedule(selectedClassId);
    else {
      setSchedule([]);
      setScheduleError("");
    }
    loadTodayAttendance(selectedClassId);
    // eslint-disable-next-line
  }, [selectedClassId, scheduleFeatureEnabled]);

  useEffect(() => {
    if (showCustomize) {
      setDraftPrefs(prefs);
    }
  }, [showCustomize, prefs]);

  useEffect(() => {
    if (!reportStudentId) {
      const source = students.length ? students : [];
      if (source.length) setReportStudentId(String(source[0].id));
    }
  }, [students, reportStudentId]);

  useEffect(() => {
    let active = true;
    const stopLoading = () => { active = false; };

    const loadOnlineClassSessions = async () => {
      try {
        const response = await api.get(API_PATHS.teacher.onlineClasses());
        const payload = extractArrayFromResponse(response, ONLINE_CLASS_COLLECTION_KEYS);
        if (active) setOnlineClassSessions(Array.isArray(payload) ? payload.map(normalizeOnlineClassSession) : []);
      } catch (error) {
        if (!active || error?.name === "AbortError") return;
        toast.error("Unable to load online classes from server.");
        if (active) setOnlineClassSessions([]);
      }
    };

    loadOnlineClassSessions();
    window.addEventListener("auth:logout", stopLoading);
    return () => {
      active = false;
      window.removeEventListener("auth:logout", stopLoading);
    };
  }, []);

  useEffect(() => {
    const refreshExpiredStatuses = () => {
      setOnlineClassSessions((current) => current.map(normalizeOnlineClassSession));
    };
    const intervalId = window.setInterval(refreshExpiredStatuses, 60 * 1000);
    return () => window.clearInterval(intervalId);
  }, []);

  const togglePref = (key) => {
    setDraftPrefs((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const resetPrefs = () => {
    setDraftPrefs(defaultTeacherPrefs);
  };

  const arePrefsEqual = (left, right) => {
    const leftKeys = Object.keys(left);
    const rightKeys = Object.keys(right);
    if (leftKeys.length !== rightKeys.length) return false;
    return leftKeys.every((key) => left[key] === right[key]);
  };

  const prefsChanged = !arePrefsEqual(draftPrefs, prefs);
  const canApply = prefsChanged;

  const applyCustomization = () => {
    setPrefs(draftPrefs);
    localStorage.setItem("teacherDashboardPrefs", JSON.stringify(draftPrefs));
    toast.success("Dashboard settings applied.");
    setShowCustomize(false);
  };

  const preferenceOptions = [
    { key: "showClassControls", label: "Class selector" },
    { key: "showSchedule", label: "Schedule panel" },
    { key: "showOnlineClasses", label: "Online Classes" },
    { key: "showStudentReports", label: "Student reports" },
    { key: "showSubmitMarks", label: "Submit marks" },
    { key: "showUploadNotes", label: "Upload notes" },
    { key: "showLeaveRequests", label: "Leave requests" },
    { key: "showHolidayRequests", label: "Holiday requests" },
    { key: "showAttendancePanel", label: "Attendance panel" },
    { key: "showPerformanceInsights", label: "Performance insights" },
    { key: "showAiRiskRadar", label: "AI at-risk radar" },
    { key: "showAssessments", label: "Assessments" },
  ];

  const showClassControls = prefs.showClassControls;
  const showSchedulePanel = prefs.showSchedule && scheduleFeatureEnabled;
  const showOnlineClasses = prefs.showOnlineClasses;
  const showStudentReports = prefs.showStudentReports;
  const showSubmitMarks = prefs.showSubmitMarks;
  const showUploadNotes = prefs.showUploadNotes;
  const showLeaveRequests = prefs.showLeaveRequests;
  const showHolidayRequests = prefs.showHolidayRequests;
  const showAttendancePanel = prefs.showAttendancePanel;
  const showPerformanceInsights = prefs.showPerformanceInsights;
  const showAiRiskRadar = prefs.showAiRiskRadar;
  const showAssessments = prefs.showAssessments;
  const overviewEnabled = showClassControls || showSchedulePanel;
  const studentsEnabled = showStudentReports || showSubmitMarks;
  const onlineClassEnabled = showOnlineClasses;
  const resourcesEnabled = showUploadNotes || showLeaveRequests || showHolidayRequests;
  const insightsEnabled = showPerformanceInsights || showAiRiskRadar;

  const onlineClassTabs = [
    { id: "schedule", label: "Schedule Live Class", icon: FaVideo },
    { id: "control", label: "Live Class Control", icon: FaBroadcastTower },
  ];
  const attendanceTabs = [
    { id: "mark", label: "Mark Attendance", icon: FaClipboardList },
    { id: "reports", label: "Attendance Reports", icon: FaFileAlt },
  ];
  const insightsTabs = [
    { id: "risk", label: "AI At-Risk Radar", icon: FaExclamationTriangle, enabled: showAiRiskRadar },
    { id: "performance", label: "Performance Insights", icon: FaChartLine, enabled: showPerformanceInsights },
  ];
  const studentsTabs = [
    { id: "reports", label: "Student Reports", icon: FaFileAlt, enabled: showStudentReports },
    { id: "marks", label: "Submit Marks", icon: FaGraduationCap, enabled: showSubmitMarks },
  ];
  const resourcesTabs = [
    { id: "notes", label: "Upload Notes", icon: FaUpload, enabled: showUploadNotes },
    { id: "leave", label: "Leave Requests", icon: FaClipboardList, enabled: showLeaveRequests },
    { id: "holiday", label: "Holiday Requests", icon: FaCalendarAlt, enabled: showHolidayRequests },
  ];

  const getVisibleTabs = (tabs) => tabs.filter((tab) => tab.enabled !== false);
  const getActiveModuleTab = (tabs, activeId) => {
    const visibleTabs = getVisibleTabs(tabs);
    if (visibleTabs.some((tab) => tab.id === activeId)) return activeId;
    return visibleTabs[0]?.id || "";
  };
  const renderTopTabs = (tabs, activeId, setActiveId) => {
    const visibleTabs = getVisibleTabs(tabs);
    if (visibleTabs.length <= 1) return null;

    return (
      <div className="overflow-x-auto border-b border-gray-100 bg-white px-6 pt-4">
        <div className="flex min-w-max gap-2">
          {visibleTabs.map((tab) => {
            const isActive = tab.id === activeId;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveId(tab.id)}
                className={`rounded-t-md border px-4 py-2.5 text-sm font-semibold transition-all duration-200 ${isActive
                    ? "border-sky-300 bg-sky-50 text-sky-700 shadow-[0_0_0_1px_rgba(14,165,233,0.22)]"
                    : "border-transparent text-gray-500 hover:bg-gray-50 hover:text-gray-700"
                  }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const selectedOnlineClassTab = getActiveModuleTab(onlineClassTabs, activeOnlineClassTab);
  const selectedAttendanceTab = getActiveModuleTab(attendanceTabs, activeAttendanceTab);
  const selectedInsightsTab = getActiveModuleTab(insightsTabs, activeInsightsTab);
  const selectedStudentsTab = getActiveModuleTab(studentsTabs, activeStudentsTab);
  const selectedResourcesTab = getActiveModuleTab(resourcesTabs, activeResourcesTab);

  const dashboardSections = [
    {
      id: "overview",
      label: "Overview",
      icon: FaHome,
      accent: "from-slate-900 to-slate-700",
      enabled: overviewEnabled,
      description: "Classes and schedule",
    },
    {
      id: "online-classes",
      label: "Online Class",
      icon: FaVideo,
      accent: "from-sky-600 to-cyan-500",
      enabled: onlineClassEnabled,
      description: "Create and run live classes",
    },
    {
      id: "attendance",
      label: "Attendance",
      icon: FaClipboardList,
      accent: "from-emerald-600 to-teal-500",
      enabled: showAttendancePanel,
      description: "Mark and export attendance",
    },
    {
      id: "insights",
      label: "Insights",
      icon: FaChartLine,
      accent: "from-indigo-600 to-violet-500",
      enabled: insightsEnabled,
      description: "Performance and risk radar",
    },
    {
      id: "account",
      label: "Account",
      icon: FaMoneyBillWave,
      accent: "from-emerald-600 to-teal-500",
      enabled: true,
      description: "Salary and payment details",
    },
    {
      id: "students",
      label: "Students",
      icon: FaGraduationCap,
      accent: "from-amber-500 to-orange-500",
      enabled: studentsEnabled,
      description: "Reports and marks",
    },
    {
      id: "resources",
      label: "Resources",
      icon: FaUpload,
      accent: "from-rose-500 to-pink-500",
      enabled: resourcesEnabled,
      description: "Notes and requests",
    },
    {
      id: "usage",
      label: "Usage",
      icon: FaClock,
      accent: "from-cyan-600 to-blue-500",
      enabled: true,
      description: "Student platform activity",
    },
    {
      id: "assessments",
      label: "Assessments",
      icon: FaClipboardList,
      accent: "from-indigo-600 to-violet-500",
      enabled: showAssessments,
      description: "Create proctored assessments",
    },
  ].filter((section) => section.enabled);

  useEffect(() => {
    const handleSidebarAction = (event) => {
      const action = event?.detail?.action;
      if (!action) return;
      setActiveSection(action);
      window.scrollTo({ top: 0, behavior: "smooth" });
    };

    window.addEventListener("teacher-dashboard:select-action", handleSidebarAction);
    return () => window.removeEventListener("teacher-dashboard:select-action", handleSidebarAction);
  }, []);

  useEffect(() => {
    if (!dashboardSections.some((section) => section.id === activeSection)) {
      setActiveSection(dashboardSections[0]?.id || "overview");
    }
  }, [activeSection, dashboardSections]);

  // --- Loader functions (try API, fallback to sample) ---
  async function loadClasses() {
    try {
      const res = await api.get(API_PATHS.teacher.classes());
      const payload = extractArrayFromResponse(res, CLASS_COLLECTION_KEYS).map(normalizeClassRecord);
      if (payload.length) {
        setClasses(payload);
        setSelectedClassId(payload[0].id);
        setUsingOfflineData(false);
        return;
      }
      throw new Error("No classes from API");
    } catch (err) {
      console.error("Classes API failed", err);

      setClasses([]);
      setSelectedClassId(null);
      setSchedule([]);
      setAttendanceRecords({});
      setUsingOfflineData(false);
      loadStudents(null);
    }
  }

  async function loadStudentsGroupedByClass() {
    try {
      const res = await api.get(API_PATHS.teacher.studentsGroupedByClass());
      const groups = extractArrayFromResponse(res).map(normalizeClassGroup);
      setClassGroups(groups);
    } catch (err) {
      console.error("Grouped students API failed", err);
      setClassGroups([]);
    }
  }

  async function loadClassesDropdown() {
    try {
      const res = await api.get(API_PATHS.teacher.classesDropdown());
      const list = extractArrayFromResponse(res).map(normalizeDropdownClass);
      setClassDropdown(list);
    } catch (err) {
      console.error("Classes dropdown API failed", err);
      setClassDropdown([]);
    }
  }

  async function loadStudents(classId = null) {
    setLoading(true);

    try {
      const hasClass = hasSelectedClassId(classId);
      // classId here is always the real teacher class id; match it to a group
      // via the class's own name/label, not the raw id, since the grouped API
      // keys its groups by className (e.g. "10"), not the teacher class id.
      const selectedClassObj = classes.find((c) => String(c.id) === String(classId));
      const classKey = normalizeClassKey(selectedClassObj?.name || classId);
      const matchedGroup = hasClass
        ? classGroups.find((group) => normalizeClassKey(group.className) === classKey)
        : null;

      if (matchedGroup) {
        setStudents(matchedGroup.students);
        setUsingOfflineData(false);
        return;
      }

      // Class students come live from the class-scoped API when a class is
      // selected; the flat roster fallback now uses the grouped-by-class API too.
      if (hasClass) {
        const res = await api.get(API_PATHS.teacher.classStudents(classId));
        const payload = extractArrayFromResponse(res, STUDENT_COLLECTION_KEYS);
        setStudents(payload.map(normalizeStudentRecord));
      } else {
        const res = await api.get(API_PATHS.teacher.studentsGroupedByClass());
        const groups = extractArrayFromResponse(res).map(normalizeClassGroup);
        setClassGroups(groups);
        setStudents(groups.flatMap((group) => group.students));
      }
      setUsingOfflineData(false);
    } catch (err) {
      console.error("Students API failed", err);
      setStudents([]);
      toast.error(resolveApiErrorMessage(err, "Unable to load students for this class."));
    } finally {
      setLoading(false);
    }
  }

  async function loadSchedule(classId) {
    setScheduleLoading(true);
    setScheduleError("");
    try {
      const res = await api.get(API_PATHS.teacher.classSchedule(classId));
      const payload = extractArrayFromResponse(res, SCHEDULE_COLLECTION_KEYS);
      setSchedule(payload.map(normalizeScheduleRecord));
    } catch (err) {
      console.error("Schedule API failed", err);
      setSchedule([]);
      setScheduleError(resolveApiErrorMessage(err, "Unable to load the schedule."));
    } finally {
      setScheduleLoading(false);
    }
  }

 async function loadTodayAttendance(classId) {
  try {
    const res = await api.get(
      API_PATHS.teacher.classAttendanceToday(classId)
    );

    const payload = extractArrayFromResponse(
      res,
      ATTENDANCE_COLLECTION_KEYS
    );

    const records = {};

    payload.forEach((a) => {
      const studentId = a.studentId
        ?? a.student_id
        ?? a.student?.id
        ?? a.learnerId
        ?? a.learner?.id;
      if (studentId === undefined || studentId === null) return;

      const attendanceId = a.id ?? a.attendanceId ?? a._id;
      records[String(studentId)] = {
        attendanceId,
        status: normalizeAttendanceStatus(a.status ?? a.attendanceStatus ?? a.value),
        correctionStatus: normalizeCorrectionStatus(a),
      };
    });

    setAttendanceRecords(records);

  } catch (err) {
    console.error("Attendance API failed", err);
    toast.error(resolveApiErrorMessage(err, "Unable to load today's attendance."));
    setAttendanceRecords({});
  }
}

  const upsertOnlineClassSessions = (updater) => {
    setOnlineClassSessions((previous) => {
      const next = typeof updater === "function" ? updater(previous) : updater;
      return next.map((session, index) => normalizeOnlineClassSession(session, index));
    });
  };

const selectedClassName =
  classOptions.find(
    (item) => String(item.id) === String(selectedClassId)
  )?.name || "Class";

  const handleCreateOnlineClass = async (event) => {
    event.preventDefault();
    if (creatingOnlineClass) return;

    if (!hasSelectedClassId(selectedClassId)) {
      toast.error("Select a class before starting the online class.");
      return;
    }
    if (!onlineClassForm.title.trim()) {
      toast.error("Add a class title before continuing.");
      return;
    }
    if (!onlineClassForm.startImmediately && !onlineClassForm.startAt) {
      toast.error("Add a start time or choose Start immediately.");
      return;
    }

    const payload = {
      title: onlineClassForm.title.trim(),
      classId: selectedClassId,
      durationMinutes: Number(onlineClassForm.durationMinutes || 60),
    };
    if (onlineClassForm.section.trim()) payload.section = onlineClassForm.section.trim();
    if (!onlineClassForm.startImmediately) payload.scheduledAt = new Date(onlineClassForm.startAt).toISOString();

    setCreatingOnlineClass(true);
    try {
      await api.post(API_PATHS.teacher.onlineClasses(), payload);
      const refresh = await api.get(API_PATHS.teacher.onlineClasses());
      const refreshPayload = extractArrayFromResponse(refresh, ONLINE_CLASS_COLLECTION_KEYS);
      setOnlineClassSessions(Array.isArray(refreshPayload) ? refreshPayload.map(normalizeOnlineClassSession) : []);
      toast.success(onlineClassForm.startImmediately ? "Online class started." : "Online class scheduled.");
      setOnlineClassForm({ title: "", startAt: "", durationMinutes: "60", section: "", startImmediately: false });
    } catch (error) {
      toast.error(error?.message || "Unable to schedule class.");
    } finally {
      setCreatingOnlineClass(false);
    }
  };

  const updateOnlineClassStatus = async (roomCode, status) => {
    const updateKey = `${roomCode}:${status}`;
    if (updatingOnlineClass) return;
    const session = onlineClassSessions.find((item) => item.roomId === roomCode);
    if (status === "live" && session?.status === "expired") {
      toast.error("This scheduled class has expired and cannot be started.");
      return;
    }
    setUpdatingOnlineClass(updateKey);
    try {
      if (status === "live") {
        await api.post(API_PATHS.teacher.startOnlineClass(roomCode));
      } else if (status === "ended") {
        await api.delete(API_PATHS.teacher.endOnlineClass(roomCode));
      } else {
        throw new Error("Unsupported classroom status update.");
      }

      const response = await api.get(API_PATHS.teacher.onlineClasses());
      const payload = extractArrayFromResponse(response, ONLINE_CLASS_COLLECTION_KEYS);
      setOnlineClassSessions(Array.isArray(payload) ? payload.map(normalizeOnlineClassSession) : []);
      if (status === "ended") {
        setActiveWebRtcSessionId((current) => current === session?.id ? "" : current);
        toast.success("Live class ended. Participants have been notified.");
      } else {
        toast.success("Live class started. Students can now join.");
      }
    } catch (error) {
      toast.error(error?.message || "Unable to update class status.");
    } finally {
      setUpdatingOnlineClass("");
    }
  };

  const copyOnlineClassRoomId = async (roomId) => {
    try {
      await navigator.clipboard.writeText(roomId);
      toast.success("Class code copied");
    } catch {
      toast.info("Unable to auto-copy. Please copy manually.");
    }
  };

  // Mark a NOT_MARKED student with the given status. Existing records require
  // an admin-approved correction workflow instead of a repeated mark call.
  const markSingleAttendance = async (studentId, status) => {
    if (!hasSelectedClassId(selectedClassId)) {
      toast.error("Select a class first.");
      return;
    }
    const key = String(studentId);
    const current = normalizeAttendanceStatus(attendanceRecords[key]?.status);
    if (current !== NOT_MARKED) {
      toast.info("Attendance is already marked for today. Request an admin correction to change it.", {
        position: "top-center",
      });
      return;
    }

    setMarkingStudentId(studentId);
    try {
      await api.post(API_PATHS.teacher.markAttendance(), {
        studentId,
        classId: selectedClassId,
        date: new Date().toISOString().split("T")[0],
        status,
      });
      toast.success(`Marked ${ATTENDANCE_STATUS_LABELS[status].toLowerCase()}`);
      await loadTodayAttendance(selectedClassId);
    } catch (err) {
      toast.error(resolveApiErrorMessage(err, "Attendance was not saved on the server."));
    } finally {
      setMarkingStudentId(null);
    }
  };

  const openProfile = (student) => {
    setActiveStudent(student);
    setShowProfile(true);
  };

  const openReportCard = (student) => {
    setActiveStudent(student);
    setShowReportCard(true);
  };

  const saveStudentUpdate = async (updatedStudent) => {
    // Backend only accepts name/className/section for this endpoint.
    const payload = {
      name: updatedStudent.name,
      className: updatedStudent.className,
      section: updatedStudent.section,
    };
    try {
      await api.put(API_PATHS.teacher.updateStudent(updatedStudent.id), payload);
      toast.success("Student updated");
      loadStudents(selectedClassId);
      setShowProfile(false);
    } catch (err) {
      toast.error(resolveApiErrorMessage(err, "Student update was not saved on the server."));
    }
  };

  const saveReportCard = async (studentId, reportData) => {
    try {
      await api.post(API_PATHS.teacher.saveReportCard(studentId), reportData);
      toast.success("Report card saved");
      setShowReportCard(false);
    } catch (err) {
      toast.error(resolveApiErrorMessage(err, "Report card was not saved on the server."));
    }
  };

  const openCorrectionRequestModal = (studentId, attendanceId) => {
    setCorrectionModal({ open: true, studentId, attendanceId, reason: "" });
  };

  const closeCorrectionModal = () => {
    if (submittingCorrection) return;
    setCorrectionModal({ open: false, studentId: null, attendanceId: null, reason: "" });
  };

  const submitCorrectionRequest = async () => {
    const reason = correctionModal.reason.trim();
    if (!reason) {
      toast.error("Please enter a reason for the correction request.");
      return;
    }

    setSubmittingCorrection(true);
    try {
      await api.post(API_PATHS.teacher.requestAttendanceCorrection(correctionModal.attendanceId), { reason });
      toast.success("Correction request submitted.");
      setCorrectionModal({ open: false, studentId: null, attendanceId: null, reason: "" });
      await loadTodayAttendance(selectedClassId);
    } catch (err) {
      const errorCode = getApiErrorCode(err);
      if (errorCode === "ATTENDANCE_CORRECTION_ALREADY_PENDING") {
        toast.error("A correction request is already pending for this attendance.");
      } else {
        toast.error(resolveApiErrorMessage(err, "Unable to submit correction request."));
      }
    } finally {
      setSubmittingCorrection(false);
    }
  };

  const applyApprovedCorrection = async (studentId, attendanceId) => {
    const status = correctionApplyStatus[String(studentId)] || "PRESENT";
    setApplyingCorrectionId(attendanceId);
    try {
      await api.put(API_PATHS.teacher.applyAttendanceCorrection(attendanceId), { status });
      toast.success("Correction applied.");
      await loadTodayAttendance(selectedClassId);
    } catch (err) {
      const errorCode = getApiErrorCode(err);
      if (errorCode === "ATTENDANCE_CORRECTION_NOT_APPROVED") {
        toast.error("This attendance correction has not been approved by an administrator yet.");
      } else {
        toast.error(resolveApiErrorMessage(err, "Unable to apply correction."));
      }
    } finally {
      setApplyingCorrectionId(null);
    }
  };

  const handleMarkSubmit = (event) => {
    event.preventDefault();
    if (!markForm.studentId || !markForm.score) {
      toast.error("Select a student and enter a score");
      return;
    }
    const selected = students.find((s) => String(s.id) === String(markForm.studentId));
    const name = selected ? `${selected.firstName} ${selected.lastName}` : "student";
    toast.success(`Marks submitted for ${name} (demo)`);
    setMarkForm({ studentId: "", subject: "Mathematics", score: "", max: "100", remarks: "" });
  };

  const handleNoteSubmit = (event) => {
    event.preventDefault();
    if (!noteForm.title || !noteForm.classId) {
      toast.error("Add a title and select a class");
      return;
    }
    const className = classes.find((c) => String(c.id) === String(noteForm.classId))?.name || "Class";
    setClassNotes((prev) => [
      { id: Date.now(), title: noteForm.title, className, date: new Date().toISOString().split("T")[0] },
      ...prev,
    ]);
    toast.success("Class notes uploaded (demo)");
    setNoteForm({ title: "", classId: "", file: null });
  };

  const handleLeaveDecision = (id, decision) => {
    setLeaveRequests((prev) => prev.filter((r) => r.id !== id));
    toast.success(`Leave request ${decision}`);
  };

  const handleHolidayDecision = (id, decision) => {
    setHolidayRequests((prev) => prev.filter((r) => r.id !== id));
    toast.success(`Holiday request ${decision}`);
  };

  const reportStudents = students.length ? students : [];
  const selectedMarkStudent = reportStudents.find(
    (student) => String(student.id) === String(markForm.studentId)
  );
  const classOptions = classes.length ? classes : [];
  // Attendance module prefers the grouped-by-class options; falls back to the teacher classes list.
  const attendanceSelectOptions = attendanceClassOptions.length ? attendanceClassOptions : classOptions;
  const loggedInTeacherEmail = String(
    teacherProfile?.email || (rawUserName.includes("@") ? rawUserName : "")
  ).toLowerCase();
  const loggedInTeacherName = teacherProfile?.name || displayName;
  const teacherPayments = useMemo(() => getTeacherPayrollPayments({
    teacherId: teacherProfile?.id,
    teacherName: loggedInTeacherName,
    teacherEmail: loggedInTeacherEmail,
  }), [teacherProfile?.id, loggedInTeacherName, loggedInTeacherEmail]);
  const latestTeacherPayment = teacherPayments[0] || null;
  const accountSummary = useMemo(() => {
    const thisMonthKey = new Date().toISOString().slice(0, 7);
    const paidThisMonth = teacherPayments
      .filter((item) => String(item.paidOn || "").startsWith(thisMonthKey))
      .reduce((sum, item) => sum + Number(item.amount || 0), 0);
    const totalPaid = teacherPayments.reduce((sum, item) => sum + Number(item.amount || 0), 0);
    return {
      paymentsCount: teacherPayments.length,
      paidThisMonth,
      totalPaid,
    };
  }, [teacherPayments]);
  const maskAccount = (value) => {
    const text = String(value || "").replace(/\s+/g, "");
    if (!text) return "-";
    if (text.length <= 4) return text;
    return `xxxxxx${text.slice(-4)}`;
  };
  const insightStudents = reportStudents.length ? reportStudents : SAMPLE_INSIGHT_STUDENTS;
  const usingSampleInsightData = reportStudents.length === 0;
  const performanceRows = insightStudents.map((student, studentIndex) => {
    const numericId = Number.parseInt(String(student.id).replace(/\D/g, ""), 10) || studentIndex + 1;
    const marks = PERFORMANCE_SUBJECTS.map((subject, index) => {
      const score = Array.isArray(student.insightScores)
        ? student.insightScores[index]
        : 65 + ((numericId * 7 + index * 11) % 31);
      return { subject, score, max: 100 };
    });
    const totalScore = marks.reduce((sum, mark) => sum + mark.score, 0);
    const percentage = marks.length ? Math.round((totalScore / (marks.length * 100)) * 100) : 0;
    return {
      student,
      marks,
      percentage,
      trendDelta: Number(student.insightTrendDelta || 0),
    };
  });
  const classAverage = performanceRows.length
    ? Math.round(performanceRows.reduce((sum, row) => sum + row.percentage, 0) / performanceRows.length)
    : 0;
  const subjectAverages = PERFORMANCE_SUBJECTS.map((subject, index) => {
    if (!performanceRows.length) return { subject, average: 0 };
    const average = Math.round(
      performanceRows.reduce((sum, row) => sum + row.marks[index].score, 0) / performanceRows.length
    );
    return { subject, average };
  });
  const topPerformers = [...performanceRows].sort((a, b) => b.percentage - a.percentage).slice(0, 3);
  const needsHelp = [...performanceRows].sort((a, b) => a.percentage - b.percentage).slice(0, 3);
  const performanceMap = new Map(performanceRows.map((row) => [row.student.id, row.percentage]));
  const attendanceStats = reportStudents.reduce(
    (acc, student) => {
      const status = normalizeAttendanceStatus(attendanceMap[student.id]);
      if (status === "PRESENT") acc.present += 1;
      else if (status === "ABSENT") acc.absent += 1;
      else if (status === "LATE") acc.late += 1;
      else if (status === "EXCUSED") acc.excused += 1;
      else acc.unmarked += 1;
      return acc;
    },
    { present: 0, absent: 0, late: 0, excused: 0, unmarked: 0 }
  );
  const selectedClassLabel = String(selectedClassName || "").trim().toLowerCase();
  const filteredOnlineClassSessions = onlineClassSessions.filter((session) => {
    if (!selectedClassId && !selectedClassLabel) return true;

    const sessionClassId = String(session.classId || "").trim();
    if (selectedClassId && sessionClassId && sessionClassId === String(selectedClassId)) {
      return true;
    }

    const sessionClassName = String(session.className || "").trim().toLowerCase();
    if (!selectedClassLabel || !sessionClassName) return false;

    return (
      sessionClassName === selectedClassLabel
      || sessionClassName.includes(selectedClassLabel)
      || selectedClassLabel.includes(sessionClassName)
    );
  });
  const visibleOnlineClassSessions = (
    filteredOnlineClassSessions.length || onlineClassSessions.length === 0
      ? filteredOnlineClassSessions
      : onlineClassSessions
  )
    .sort((left, right) => new Date(right.startAt).getTime() - new Date(left.startAt).getTime());
  const onlineClassSummary = visibleOnlineClassSessions.reduce(
    (acc, session) => {
      if (session.status === "live") acc.live += 1;
      else if (session.status === "ended") acc.ended += 1;
      else if (session.status === "expired") acc.expired += 1;
      else acc.scheduled += 1;
      return acc;
    },
    { live: 0, scheduled: 0, expired: 0, ended: 0 }
  );

  const formatDate = (date) => date.toLocaleDateString();

  const getStatusForDay = (studentId, daysAgo) => {
    if (daysAgo === 0) {
      const todayStatus = normalizeAttendanceStatus(attendanceMap[studentId]);
      if (todayStatus !== NOT_MARKED) return todayStatus;
    }
    const seed = (studentId + daysAgo) % 4;
    return seed === 0 || seed === 3 ? "ABSENT" : "PRESENT";
  };

  const riskInsights = insightStudents.map((student) => {
    const sampleAttendance = Number(student.insightAttendance);
    const hasSampleAttendance = Number.isFinite(sampleAttendance);
    let present = 0;
    let absent = 0;
    if (!hasSampleAttendance) {
      for (let daysAgo = 0; daysAgo <= 6; daysAgo += 1) {
        const status = getStatusForDay(student.id, daysAgo);
        if (status === "PRESENT") present += 1;
        else absent += 1;
      }
    }
    const total = present + absent;
    const attendancePercent = hasSampleAttendance ? sampleAttendance : total ? Math.round((present / total) * 100) : 0;
    const marksPercent = performanceMap.get(student.id) || 0;
    const absentToday = hasSampleAttendance ? Boolean(student.insightAbsentToday) : normalizeAttendanceStatus(attendanceMap[student.id]) === "ABSENT";
    let riskScore = 0;
    if (attendancePercent < 75) riskScore += 40;
    if (marksPercent < 60) riskScore += 40;
    if (absentToday) riskScore += 20;
    const riskLevel = riskScore >= 70 ? "High" : riskScore >= 40 ? "Medium" : "Low";
    const reasons = [];
    if (attendancePercent < 75) reasons.push(`Attendance ${attendancePercent}%`);
    if (marksPercent < 60) reasons.push(`Marks ${marksPercent}%`);
    if (absentToday) reasons.push("Absent today");
    if (!reasons.length) reasons.push("Stable performance");
    const action =
      riskLevel === "High"
        ? "Call parent and assign remedial pack"
        : riskLevel === "Medium"
          ? "Schedule support session"
          : "Share encouragement note";
    return {
      student,
      attendancePercent,
      marksPercent,
      riskScore,
      riskLevel,
      reasons,
      action,
    };
  });
  const riskHighlights = [...riskInsights].sort((a, b) => b.riskScore - a.riskScore).slice(0, 5);
  const riskCounts = riskInsights.reduce(
    (acc, item) => {
      const key = item.riskLevel.toLowerCase();
      acc[key] += 1;
      return acc;
    },
    { high: 0, medium: 0, low: 0 }
  );
  const averageInsightTrend = performanceRows.length
    ? Math.round(performanceRows.reduce((sum, row) => sum + row.trendDelta, 0) / performanceRows.length)
    : 0;
  const averageAttendance = riskInsights.length
    ? Math.round(riskInsights.reduce((sum, item) => sum + item.attendancePercent, 0) / riskInsights.length)
    : 0;

  const buildStudentReport = (student) => {
    const daily = [];
    for (let daysAgo = 6; daysAgo >= 0; daysAgo -= 1) {
      const date = new Date();
      date.setDate(date.getDate() - daysAgo);
      daily.push({
        date: formatDate(date),
        status: getStatusForDay(student.id, daysAgo),
      });
    }
    const present = daily.filter((entry) => entry.status === "PRESENT").length;
    const absent = daily.filter((entry) => entry.status !== "PRESENT").length;
    const total = daily.length;
    const percentage = total ? Math.round((present / total) * 100) : 0;
    return { student, daily, summary: { present, absent, total, percentage } };
  };

  const buildClassReport = () =>
    reportStudents.map((student) => {
      let present = 0;
      let absent = 0;
      for (let daysAgo = 0; daysAgo <= 6; daysAgo += 1) {
        const status = getStatusForDay(student.id, daysAgo);
        if (status === "PRESENT") present += 1;
        else absent += 1;
      }
      const total = present + absent;
      const percentage = total ? Math.round((present / total) * 100) : 0;
      return {
        id: student.id,
        name: `${student.firstName} ${student.lastName}`.trim(),
        rollNumber: student.rollNumber || "—",
        present,
        absent,
        total,
        percentage,
      };
    });

  // The backend mark-all endpoint is all-or-nothing: it rejects the whole
  // request if any attendance already exists for the class/date, so we never
  // claim partial success and always resync from the server afterwards.
  const markAllAttendance = async (defaultStatus) => {
    if (!hasSelectedClassId(selectedClassId)) {
      toast.error("Select a class first.");
      return;
    }

    setMarkingAll(true);
    try {
      await api.post(API_PATHS.teacher.markAllAttendance(), {
        classId: selectedClassId,
        date: new Date().toISOString().split("T")[0],
        defaultStatus,
      });
      toast.success(`Marked ${ATTENDANCE_STATUS_LABELS[defaultStatus].toLowerCase()} for all students.`);
      await loadTodayAttendance(selectedClassId);
    } catch (err) {
      const errorCode = getApiErrorCode(err);
      if (errorCode === "ATTENDANCE_ALREADY_EXISTS") {
        toast.error("Attendance has already been marked for one or more students. No attendance was changed.");
      } else {
        toast.error(resolveApiErrorMessage(err, "Bulk attendance was not saved on the server."));
      }
      await loadTodayAttendance(selectedClassId);
    } finally {
      setMarkingAll(false);
    }
  };

  const clearAttendance = () => {
    toast.info("Attendance records cannot be cleared by a teacher. Ask an admin to approve a correction.", {
      position: "top-center",
    });
  };

  const refreshAttendanceData = async () => {
    const hasClass = hasSelectedClassId(selectedClassId);

    await loadStudents(hasClass ? selectedClassId : null);

    if (hasClass) {
      await loadTodayAttendance(selectedClassId);
    } else {
      setAttendanceRecords({});
    }

    setAttendanceReport(null);
    toast.success("Attendance data refreshed");
  };

  const generateStudentReport = () => {
    const student = reportStudents.find((s) => String(s.id) === String(reportStudentId));
    if (!student) {
      toast.error("Select a student to generate the report");
      return;
    }
    const report = buildStudentReport(student);
    setAttendanceReport({
      type: "student",
      generatedAt: new Date(),
      ...report,
    });
    toast.success("Student attendance report generated");
  };

  const generateClassReport = () => {
    const rows = buildClassReport();
    setAttendanceReport({
      type: "class",
      generatedAt: new Date(),
      rows,
    });
    toast.success("Class attendance report generated");
  };

  const exportAttendanceReport = () => {
    if (!attendanceReport) {
      toast.info("Generate a report first");
      return;
    }
    let csv = "";
    if (attendanceReport.type === "student") {
      csv = [
        ["date", "status"],
        ...(attendanceReport.daily || []).map((row) => [row.date, row.status]),
      ]
        .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
        .join("\n");
    } else {
      csv = [
        ["name", "roll", "present", "absent", "total", "percentage"],
        ...(attendanceReport.rows || []).map((row) => [
          row.name,
          row.rollNumber,
          row.present,
          row.absent,
          row.total,
          `${row.percentage}%`,
        ]),
      ]
        .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
        .join("\n");
    }

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const suffix = attendanceReport.type === "student" ? "student" : "class";
    a.href = url;
    a.download = `attendance_report_${suffix}_${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Report exported");
  };

  // ---------- RENDER ----------
  return (
    <div className="teacher-dashboard-square min-h-screen bg-gradient-to-br from-slate-50 via-amber-50/40 to-slate-100 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => setShowCustomize((prev) => !prev)}
            className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:bg-slate-50"
          >
            <FaSlidersH />
            {showCustomize ? "Close" : "Customize"}
          </button>
        </div>

        {showCustomize && (
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">Customize</p>
                <h2 className="text-lg font-semibold text-slate-900">Personalize your dashboard</h2>
                <p className="text-sm text-slate-600">Select what you want to see, then apply changes.</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={resetPrefs}
                  className="rounded-md border border-slate-200 px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"
                >
                  Reset
                </button>
                <button
                  type="button"
                  onClick={applyCustomization}
                  disabled={!canApply}
                  className={`rounded-md px-3 py-2 text-sm font-medium transition ${canApply
                      ? "bg-emerald-600 text-white hover:bg-emerald-700"
                      : "cursor-not-allowed bg-slate-200 text-slate-500"
                    }`}
                >
                  Apply changes
                </button>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {preferenceOptions.map((option) => (
                <label
                  key={option.key}
                  className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-sm transition ${draftPrefs[option.key] ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-white"
                    }`}
                >
                  <input
                    type="checkbox"
                    checked={draftPrefs[option.key]}
                    onChange={() => togglePref(option.key)}
                    disabled={option.key === "showSchedule" && !scheduleFeatureEnabled}
                    className="mt-1 h-4 w-4 accent-emerald-600"
                  />
                  <span className="text-slate-700">{option.label}{option.key === "showSchedule" && !scheduleFeatureEnabled ? " (disabled by school)" : ""}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Offline banner */}
        {usingOfflineData && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-amber-800">
            Using offline sample data or changes couldn't be synced — the UI will work locally.
          </div>
        )}



        {/* Controls: Class select & Schedule */}
        {activeSection === "overview" && (showClassControls || showSchedulePanel) && (
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            {showClassControls && (
              <div className="flex flex-wrap gap-3 items-center rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
                <label className="text-sm text-slate-600">Select Class</label>
                <select
                  value={selectedClassId ?? ""}
                  onChange={(e) => setSelectedClassId(e.target.value)}
                  className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                >
                  {classOptions.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {showSchedulePanel && (
              <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm text-sm w-full md:w-1/2">
                <div className="font-semibold text-slate-700">Today's Schedule</div>
                <div className="mt-1 text-slate-600">
                  {tenantFeaturesLoading ? (
                    <div className="text-slate-400">Checking availability...</div>
                  ) : scheduleLoading ? (
                    <div className="text-slate-400">Loading schedule...</div>
                  ) : scheduleError ? (
                    <div className="text-rose-500">{scheduleError}</div>
                  ) : schedule.length ? (
                    schedule.map((s) => (
                      <div key={s.id} className="flex justify-between items-center">
                        <div>{s.title}</div>
                        <div className="text-xs text-slate-500">{s.time}</div>
                      </div>
                    ))
                  ) : (
                    <div className="text-slate-400">No schedule</div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {activeSection === "overview" && (
          <div className="grid gap-4 grid-cols-2 xl:grid-cols-4">
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Selected Class</p>
              <p className="mt-2 text-2xl font-semibold text-slate-900">
                {classOptions.find((item) => item.id === selectedClassId)?.name || "Not selected"}
              </p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Students</p>
              <p className="mt-2 text-2xl font-semibold text-slate-900">{reportStudents.length}</p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Pending Leaves</p>
              <p className="mt-2 text-2xl font-semibold text-slate-900">{leaveRequests.length}</p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Class Average</p>
              <p className="mt-2 text-2xl font-semibold text-slate-900">{classAverage}%</p>
            </div>
          </div>
        )}

        {activeSection === "online-classes" && showOnlineClasses && (
          <div className="space-y-5">
            {renderTopTabs(onlineClassTabs, selectedOnlineClassTab, setActiveOnlineClassTab)}
            {selectedOnlineClassTab === "schedule" && (
            <div className="rounded-3xl border border-sky-100 bg-[linear-gradient(160deg,#ffffff_0%,#f0f9ff_55%,#e0f2fe_100%)] p-6 shadow-[0_24px_60px_rgba(2,132,199,0.15)]">
              <div className="flex items-center gap-2 text-slate-800 font-semibold">
                <FaVideo />
                Schedule Live Class
              </div>
              <p className="mt-1 text-xs uppercase tracking-[0.16em] text-sky-700/80">
                Start a native browser video class for {selectedClassName}.
              </p>

              <form onSubmit={handleCreateOnlineClass} className="mt-4 space-y-3 text-sm">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <label className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                    School class <span className="text-rose-500">*</span>
                    <select
                      value={selectedClassId ?? ""}
                      disabled={creatingOnlineClass || !classOptions.length}
                      required
                      onChange={(event) => setSelectedClassId(event.target.value)}
                      className="mt-1.5 w-full rounded-xl border border-sky-200 bg-white px-3 py-2.5 text-sm normal-case tracking-normal text-slate-900 shadow-sm outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-100 disabled:bg-slate-100 disabled:text-slate-500"
                    >
                      <option value="">Select a class</option>
                      {classOptions.map((item) => (
                        <option key={item.id} value={item.id}>{item.name}</option>
                      ))}
                    </select>
                  </label>
                  <label className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                    Class title <span className="text-rose-500">*</span>
                    <input
                      value={onlineClassForm.title}
                      disabled={creatingOnlineClass}
                      required
                      onChange={(event) =>
                        setOnlineClassForm((previous) => ({ ...previous, title: event.target.value }))
                      }
                      className="mt-1.5 w-full rounded-xl border border-sky-200 bg-white/95 px-3 py-2.5 text-sm normal-case tracking-normal shadow-sm outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
                      placeholder="e.g., Algebra Revision"
                    />
                  </label>
                </div>
                {!classOptions.length && (
                  <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" role="alert">
                    <span>No classes are assigned to this teacher. Assign a class in school administration, then retry.</span>
                    <button type="button" onClick={() => { loadClasses(); loadClassesDropdown(); }} className="font-bold underline underline-offset-2">Retry</button>
                  </div>
                )}
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <label className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                    Date and time
                    <input
                      type="datetime-local"
                      value={onlineClassForm.startAt}
                      disabled={onlineClassForm.startImmediately || creatingOnlineClass}
                      onChange={(event) =>
                        setOnlineClassForm((previous) => ({ ...previous, startAt: event.target.value }))
                      }
                      className={`mt-1.5 w-full rounded-xl border bg-white px-3 py-2.5 text-sm normal-case tracking-normal text-slate-900 shadow-sm outline-none transition ${onlineClassForm.startAt && !onlineClassForm.startImmediately ? "border-sky-500 ring-2 ring-sky-200" : "border-sky-200 focus:border-sky-400 focus:ring-2 focus:ring-sky-100"}`}
                    />
                  </label>
                  <label className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                    Duration (minutes)
                    <input
                      type="number"
                      min="15"
                      max="240"
                      value={onlineClassForm.durationMinutes}
                      disabled={creatingOnlineClass}
                      onChange={(event) =>
                        setOnlineClassForm((previous) => ({ ...previous, durationMinutes: event.target.value }))
                      }
                      className="mt-1.5 w-full rounded-xl border border-sky-200 bg-white px-3 py-2.5 text-sm normal-case tracking-normal text-slate-900 shadow-sm outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
                    />
                  </label>
                </div>
                {!onlineClassForm.startImmediately && onlineClassForm.startAt && (
                  <div className="rounded-xl border border-sky-300 bg-sky-100 px-3 py-2 text-sm font-semibold text-sky-900" role="status">
                    Selected time slot: {new Date(onlineClassForm.startAt).toLocaleString()} · {onlineClassForm.durationMinutes || 60} minutes
                  </div>
                )}
                <input
                  value={onlineClassForm.section}
                  disabled={creatingOnlineClass}
                  onChange={(event) => setOnlineClassForm((previous) => ({ ...previous, section: event.target.value }))}
                  className="w-full rounded-xl border border-sky-200 bg-white/95 px-3 py-2.5 shadow-sm outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
                  placeholder="Section (optional; blank means all sections)"
                />
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input type="checkbox" checked={onlineClassForm.startImmediately} disabled={creatingOnlineClass} onChange={(event) => setOnlineClassForm((previous) => ({ ...previous, startImmediately: event.target.checked }))} className="h-4 w-4 accent-sky-600" />
                  Start immediately
                </label>
                <button
                  type="submit"
                  disabled={creatingOnlineClass}
                  aria-busy={creatingOnlineClass}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-sky-600 px-3 py-2.5 text-sm font-semibold text-white shadow-lg shadow-sky-300/50 transition hover:-translate-y-0.5 hover:bg-sky-700 disabled:cursor-wait disabled:translate-y-0 disabled:bg-sky-400"
                >
                  {creatingOnlineClass && <FaSpinner className="animate-spin" aria-hidden="true" />}
                  {creatingOnlineClass
                    ? onlineClassForm.startImmediately ? "Starting class..." : "Scheduling class..."
                    : onlineClassForm.startImmediately ? "Start class" : "Schedule class"}
                </button>
              </form>

              <div className="mt-4 flex flex-wrap gap-2 text-xs font-semibold">
                <span className="rounded-full border border-sky-200 bg-sky-100 px-3 py-1 text-sky-800">Live {onlineClassSummary.live}</span>
                <span className="rounded-full border border-amber-200 bg-amber-100 px-3 py-1 text-amber-800">Scheduled {onlineClassSummary.scheduled}</span>
                <span className="rounded-full border border-rose-200 bg-rose-100 px-3 py-1 text-rose-800">Expired {onlineClassSummary.expired}</span>
                <span className="rounded-full border border-slate-300 bg-slate-100 px-3 py-1 text-slate-700">Ended {onlineClassSummary.ended}</span>
              </div>
            </div>
            )}

            {selectedOnlineClassTab === "control" && (
            <div className="rounded-3xl border border-slate-200 bg-[linear-gradient(175deg,#ffffff_0%,#f8fafc_100%)] p-6 shadow-[0_24px_60px_rgba(15,23,42,0.1)]">
              <div className="flex items-center gap-2 text-slate-800 font-semibold">
                <FaBroadcastTower />
                Live Class Control
              </div>
              <p className="mt-1 text-xs uppercase tracking-[0.16em] text-slate-500">Start, end, and share room IDs to let students attend class live.</p>

              <div className="mt-4 space-y-3">
                {visibleOnlineClassSessions.length === 0 && (
                  <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-4 py-6 text-sm text-slate-500">
                    No online sessions yet for this class.
                  </div>
                )}

                {visibleOnlineClassSessions.map((session) => {
                  const statusClass =
                    session.status === "live"
                      ? "bg-emerald-100 text-emerald-700"
                      : session.status === "ended"
                        ? "bg-slate-200 text-slate-700"
                        : session.status === "expired"
                          ? "bg-rose-100 text-rose-700"
                        : "bg-amber-100 text-amber-700";

                  return (
                    <div key={session.id} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div>
                          <p className="text-sm font-semibold text-slate-900 tracking-wide">{session.title}</p>
                          <p className="text-xs text-slate-500">
                            {session.className} • {new Date(session.startAt).toLocaleString()} • {session.durationMinutes} min
                          </p>
                          <p className="text-xs text-slate-500">Start deadline {new Date(session.scheduledEndAt).toLocaleString()}</p>
                          <p className="text-xs text-slate-500">Class code {session.roomId}</p>
                        </div>
                        <span className={`rounded-full px-3 py-1 text-xs font-semibold ${statusClass}`}>
                          {session.status.toUpperCase()}
                        </span>
                      </div>

                      <div className="mt-3 flex flex-wrap items-center gap-2">
                        {session.status === "scheduled" && (
                          <button
                            type="button"
                            onClick={() => updateOnlineClassStatus(session.roomId, "live")}
                            disabled={Boolean(updatingOnlineClass)}
                            className="inline-flex items-center gap-1 rounded-full bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white shadow transition hover:bg-emerald-700 disabled:cursor-wait disabled:opacity-60"
                          >
                            {updatingOnlineClass === `${session.roomId}:live` ? <FaSpinner className="animate-spin" /> : <FaPlayCircle />}
                            {updatingOnlineClass === `${session.roomId}:live` ? "Starting..." : "Start"}
                          </button>
                        )}
                        {session.status === "live" && (
                          <button
                            type="button"
                            onClick={() => updateOnlineClassStatus(session.roomId, "ended")}
                            disabled={Boolean(updatingOnlineClass)}
                            className="inline-flex items-center gap-1 rounded-full bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white shadow transition hover:bg-rose-700 disabled:cursor-wait disabled:opacity-60"
                          >
                            {updatingOnlineClass === `${session.roomId}:ended` ? <FaSpinner className="animate-spin" /> : <FaStopCircle />}
                            {updatingOnlineClass === `${session.roomId}:ended` ? "Ending..." : "End"}
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => copyOnlineClassRoomId(session.roomId)}
                          className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-100"
                        >
                          <FaCopy />
                          Copy class code
                        </button>
                        <button
                          type="button"
                          onClick={() => navigate(`/classroom/room/${encodeURIComponent(session.roomId)}`)}
                          disabled={session.status !== "live"}
                          className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <FaExternalLinkAlt />
                          Enter classroom
                        </button>
                        <span className="ml-auto text-xs font-medium text-slate-500">
                          Attendees {(session.attendees || []).length}
                        </span>
                      </div>

                      {false && activeWebRtcSessionId === session.id && session.status === "live" && (
                        <WebRTCClassRoom
                          roomId={session.roomId}
                          role="teacher"
                          displayName={displayName}
                          onParticipantJoin={(meta) => {
                            const attendeeId = String(meta?.name || "participant").toLowerCase();
                            upsertOnlineClassSessions((previous) =>
                              previous.map((item) => {
                                if (item.id !== session.id) return item;
                                const filtered = (item.attendees || []).filter(
                                  (entry) => String(entry.id || "").toLowerCase() !== attendeeId
                                );
                                return {
                                  ...item,
                                  attendees: [
                                    {
                                      id: attendeeId,
                                      name: meta?.name || "Participant",
                                      joinedAt: new Date().toISOString(),
                                    },
                                    ...filtered,
                                  ],
                                };
                              })
                            );
                          }}
                          onClose={() => setActiveWebRtcSessionId("")}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            )}
          </div>
        )}

        {activeSection === "attendance" && showAttendancePanel && (
          <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-xl shadow-slate-200/50">
            {renderTopTabs(attendanceTabs, selectedAttendanceTab, setActiveAttendanceTab)}
            {selectedAttendanceTab === "mark" && (
              <>
            <div className="border-b border-slate-200 bg-gradient-to-br from-blue-700 via-blue-600 to-teal-600 p-5 text-white sm:p-6">
              <div className="flex flex-col gap-5 xl:flex-row xl:items-center xl:justify-between">
              <div>
                <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-blue-100">
                  <FaClipboardList />
                  Attendance workspace
                </p>
                <h2 className="mt-2 text-2xl font-black tracking-tight sm:text-3xl">Mark today’s attendance</h2>
                <p className="mt-2 text-sm text-blue-100">Review the selected class and update each learner in one click.</p>
                {attendanceSelectOptions.length > 0 && (
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <label htmlFor="attendance-class-select" className="text-xs font-bold uppercase tracking-[0.16em] text-blue-100">
                      Class
                    </label>
                    <select
                      id="attendance-class-select"
                      value={selectedClassId ?? ""}
                      onChange={(e) => setSelectedClassId(e.target.value)}
                      className="rounded-full border border-white/20 bg-white/10 px-3 py-2 text-xs font-bold text-white [&>option]:text-slate-900"
                    >
                      {attendanceSelectOptions.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1.5 text-xs font-bold text-blue-50">
                  <FaCalendarAlt className="text-teal-200" />
                  {new Date().toLocaleDateString()}
                </span>
                <button
                  type="button"
                  onClick={refreshAttendanceData}
                  disabled={loading}
                  className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-2 text-xs font-bold text-white transition hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <FaSync className={loading ? "animate-spin" : ""} />
                  Refresh
                </button>
                <select
                  value={markAllStatus}
                  onChange={(event) => setMarkAllStatus(event.target.value)}
                  className="rounded-full border border-white/20 bg-white/10 px-3 py-2 text-xs font-bold text-white [&>option]:text-slate-900"
                >
                  {ATTENDANCE_STATUS_VALUES.map((value) => (
                    <option key={value} value={value}>{ATTENDANCE_STATUS_LABELS[value]}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => markAllAttendance(markAllStatus)}
                  disabled={markingAll}
                  className="rounded-full bg-white px-3 py-2 text-xs font-bold text-blue-700 transition hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {markingAll ? "Marking all..." : "Mark All"}
                </button>
                <button
                  type="button"
                  onClick={clearAttendance}
                  className="rounded-full border border-white/20 px-3 py-2 text-xs font-bold text-white transition hover:bg-white/10"
                >
                  Clear
                </button>
              </div>
              </div>
            </div>

            <div className="grid gap-4 border-b border-slate-200 p-5 sm:grid-cols-3 sm:p-6 lg:grid-cols-5">
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-emerald-700">Present</p>
                <p className="mt-2 text-3xl font-black text-emerald-700">{attendanceStats.present}</p>
              </div>
              <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4">
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-rose-700">Absent</p>
                <p className="mt-2 text-3xl font-black text-rose-700">{attendanceStats.absent}</p>
              </div>
              <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-amber-700">Late</p>
                <p className="mt-2 text-3xl font-black text-amber-700">{attendanceStats.late}</p>
              </div>
              <div className="rounded-2xl border border-sky-200 bg-sky-50 p-4">
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-sky-700">Excused</p>
                <p className="mt-2 text-3xl font-black text-sky-700">{attendanceStats.excused}</p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Not Marked</p>
                <p className="mt-2 text-3xl font-black text-slate-700">{attendanceStats.unmarked}</p>
              </div>
            </div>

            <div className="p-5 sm:p-6">
            {reportStudents.length > 0 && !loading && Object.keys(attendanceRecords).length === 0 && (
              <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm font-semibold text-slate-600">
                Attendance has not been marked yet.
              </div>
            )}
            <div className="divide-y divide-slate-100 overflow-hidden rounded-2xl border border-slate-200">
              {loading && <div className="p-5 text-sm font-semibold text-slate-500">Loading students...</div>}
              {!loading && reportStudents.length === 0 && (
                <div className="p-8 text-center">
                  <FaCalendarAlt className="mx-auto text-3xl text-slate-300" />
                  <p className="mt-3 text-sm font-bold text-slate-700">No students available</p>
                  <p className="mt-1 text-sm text-slate-500">Select a class with enrolled students to mark attendance.</p>
                </div>
              )}
              {!loading &&
                reportStudents.map((student) => {
                  const record = attendanceRecords[String(student.id)];
                  const status = normalizeAttendanceStatus(record?.status);
                  const label = ATTENDANCE_STATUS_LABELS[status];
                  const badgeClass = ATTENDANCE_STATUS_BADGE_CLASSES[status];
                  const correctionStatus = record?.correctionStatus || null;
                  const isMarking = markingStudentId === student.id;
                  return (
                    <div
                      key={student.id}
                      className="flex flex-col gap-4 bg-white px-4 py-4 transition hover:bg-slate-50 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-sm font-black text-blue-700">
                          {student.firstName?.[0] || <FaUserCircle />}
                        </div>
                        <div className="min-w-0">
                          <div className="truncate font-bold text-slate-900">
                            {student.firstName} {student.lastName}
                          </div>
                          <div className="text-xs font-medium text-slate-500">
                            {student.rollNumber ? `Roll: ${student.rollNumber}` : student.email}
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`rounded-full px-3.5 py-2 text-xs font-bold ${badgeClass}`}>
                          {label}
                        </span>

                        {status === NOT_MARKED && (
                          <div className="flex flex-wrap items-center gap-1.5">
                            {ATTENDANCE_STATUS_VALUES.map((value) => (
                              <button
                                key={value}
                                type="button"
                                onClick={() => markSingleAttendance(student.id, value)}
                                disabled={isMarking}
                                className="rounded-full border border-slate-200 px-3 py-1.5 text-[11px] font-bold text-slate-600 transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-60"
                                title={`Mark ${ATTENDANCE_STATUS_LABELS[value]}`}
                              >
                                {isMarking ? "..." : ATTENDANCE_STATUS_LABELS[value]}
                              </button>
                            ))}
                          </div>
                        )}

                        {status !== NOT_MARKED && !correctionStatus && (
                          <button
                            type="button"
                            onClick={() => openCorrectionRequestModal(student.id, record?.attendanceId)}
                            disabled={!record?.attendanceId}
                            className="rounded-full border border-slate-200 px-3.5 py-2 text-xs font-bold text-slate-600 transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            Request Correction
                          </button>
                        )}

                        {correctionStatus === "PENDING" && (
                          <span className="rounded-full border border-amber-200 bg-amber-50 px-3.5 py-2 text-xs font-bold text-amber-700">
                            Correction Pending
                          </span>
                        )}

                        {correctionStatus === "APPROVED" && (
                          <div className="flex flex-wrap items-center gap-1.5">
                            <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3.5 py-2 text-xs font-bold text-emerald-700">
                              Correction Approved
                            </span>
                            <select
                              value={correctionApplyStatus[String(student.id)] || "PRESENT"}
                              onChange={(event) =>
                                setCorrectionApplyStatus((prev) => ({ ...prev, [String(student.id)]: event.target.value }))
                              }
                              className="rounded-full border border-slate-200 px-2 py-1.5 text-xs font-bold text-slate-600"
                            >
                              {ATTENDANCE_STATUS_VALUES.map((value) => (
                                <option key={value} value={value}>{ATTENDANCE_STATUS_LABELS[value]}</option>
                              ))}
                            </select>
                            <button
                              type="button"
                              onClick={() => applyApprovedCorrection(student.id, record?.attendanceId)}
                              disabled={applyingCorrectionId === record?.attendanceId}
                              className="rounded-full bg-emerald-600 px-3.5 py-2 text-xs font-bold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              {applyingCorrectionId === record?.attendanceId ? "Applying..." : "Apply"}
                            </button>
                          </div>
                        )}

                        {correctionStatus === "COMPLETED" && (
                          <span className="rounded-full border border-slate-200 bg-slate-100 px-3.5 py-2 text-xs font-bold text-slate-600">
                            Correction Completed
                          </span>
                        )}

                        <button
                          type="button"
                          onClick={() => openProfile(student)}
                          className="rounded-full border border-slate-200 px-3.5 py-2 text-xs font-bold text-slate-600 transition hover:bg-white"
                        >
                          Profile
                        </button>
                        <button
                          type="button"
                          onClick={() => openReportCard(student)}
                          className="rounded-full border border-slate-200 px-3.5 py-2 text-xs font-bold text-slate-600 transition hover:bg-white"
                        >
                          Report
                        </button>
                      </div>
                    </div>
                  );
                })}
            </div>
            </div>
              </>
            )}


            {selectedAttendanceTab === "reports" && (
            <div className="p-5 sm:p-6">
              <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-5">
              <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
                <div>
                  <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
                    <FaFileAlt />
                    Reports
                  </p>
                  <h2 className="mt-2 text-2xl font-black tracking-tight text-slate-900">Attendance reports</h2>
                  <p className="mt-1 text-sm text-slate-500">Generate student or class summaries and export the current result.</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <select
                    value={reportStudentId}
                    onChange={(e) => setReportStudentId(e.target.value)}
                    className="rounded-full border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700"
                  >
                    {reportStudents.map((student) => (
                      <option key={student.id} value={student.id}>
                        {student.firstName} {student.lastName}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={generateStudentReport}
                    className="rounded-full bg-blue-600 px-3 py-2 text-xs font-bold text-white transition hover:bg-blue-700"
                  >
                    Student report
                  </button>
                  <button
                    type="button"
                    onClick={generateClassReport}
                    className="rounded-full bg-slate-900 px-3 py-2 text-xs font-bold text-white transition hover:bg-slate-800"
                  >
                    Class report
                  </button>
                  <button
                    type="button"
                    onClick={exportAttendanceReport}
                    className="rounded-full border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-600 transition hover:bg-slate-50"
                  >
                    Export CSV
                  </button>
                </div>
              </div>

              {!attendanceReport && (
                <div className="mt-5 rounded-2xl border border-dashed border-slate-300 bg-white px-5 py-8 text-center">
                  <p className="text-sm font-bold text-slate-700">No report generated yet</p>
                  <p className="mt-1 text-sm text-slate-500">Choose a report type to view attendance results.</p>
                </div>
              )}

              {attendanceReport?.type === "student" && (
                <div className="mt-5">
                  <div className="grid gap-3 sm:grid-cols-3">
                    <span className="rounded-2xl bg-emerald-50 px-3 py-2 text-sm font-bold text-emerald-700">
                      Present {attendanceReport.summary.present}
                    </span>
                    <span className="rounded-2xl bg-rose-50 px-3 py-2 text-sm font-bold text-rose-700">
                      Absent {attendanceReport.summary.absent}
                    </span>
                    <span className="rounded-2xl bg-slate-100 px-3 py-2 text-sm font-bold text-slate-600">
                      Attendance {attendanceReport.summary.percentage}%
                    </span>
                  </div>
                  <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200 bg-white">
                    <table className="w-full text-left text-xs">
                      <thead className="bg-slate-100 text-slate-600">
                        <tr>
                          <th className="px-3 py-2">Date</th>
                          <th className="px-3 py-2">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {attendanceReport.daily.map((row) => (
                          <tr key={row.date} className="border-t border-slate-100">
                            <td className="px-3 py-2 text-slate-700">{row.date}</td>
                            <td className="px-3 py-2 text-slate-700">
                              {ATTENDANCE_STATUS_LABELS[row.status] || "Not Marked"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {attendanceReport?.type === "class" && (
                <div className="mt-5 overflow-hidden rounded-2xl border border-slate-200 bg-white">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-slate-100 text-slate-600">
                      <tr>
                        <th className="px-3 py-2">Student</th>
                        <th className="px-3 py-2">Roll</th>
                        <th className="px-3 py-2">Present</th>
                        <th className="px-3 py-2">Absent</th>
                        <th className="px-3 py-2">Total</th>
                        <th className="px-3 py-2">%</th>
                      </tr>
                    </thead>
                    <tbody>
                      {attendanceReport.rows.map((row) => (
                        <tr key={row.id} className="border-t border-slate-100">
                          <td className="px-3 py-2 text-slate-700">{row.name}</td>
                          <td className="px-3 py-2 text-slate-500">{row.rollNumber}</td>
                          <td className="px-3 py-2 text-slate-700">{row.present}</td>
                          <td className="px-3 py-2 text-slate-700">{row.absent}</td>
                          <td className="px-3 py-2 text-slate-700">{row.total}</td>
                          <td className="px-3 py-2 text-slate-700">{row.percentage}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              </div>
            </div>
            )}
          </div>
        )}

        {activeSection === "insights" && insightsEnabled && (
          <div className="overflow-hidden border border-slate-200 bg-white shadow-sm">
            {renderTopTabs(insightsTabs, selectedInsightsTab, setActiveInsightsTab)}
            {selectedInsightsTab === "risk" && showAiRiskRadar && (
              <div>
                <div className="grid gap-5 border-b border-slate-800 bg-slate-950 px-5 py-6 text-white lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
                  <div>
                    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-cyan-300">
                      <FaBolt />
                      Signal board
                    </div>
                    <h2 className="mt-2 text-2xl font-semibold">Student Risk Radar</h2>
                    <p className="mt-2 max-w-2xl text-sm text-slate-300">
                      Attendance, assessment, and recent activity signals combined into a practical follow-up queue.
                    </p>
                  </div>
                  <span className="w-fit border border-cyan-300/40 bg-cyan-400/10 px-3 py-1.5 text-xs font-semibold text-cyan-100">
                    {usingSampleInsightData ? "Sample signal data" : "Live class signals"}
                  </span>
                </div>

                <div className="grid divide-y divide-slate-200 border-b border-slate-200 sm:grid-cols-3 sm:divide-x sm:divide-y-0">
                  <div className="px-5 py-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">High priority</p>
                    <p className="mt-1 text-3xl font-semibold text-rose-600">{riskCounts.high}</p>
                  </div>
                  <div className="px-5 py-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Watch list</p>
                    <p className="mt-1 text-3xl font-semibold text-amber-600">{riskCounts.medium}</p>
                  </div>
                  <div className="px-5 py-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Class attendance</p>
                    <p className="mt-1 text-3xl font-semibold text-emerald-600">{averageAttendance}%</p>
                  </div>
                </div>

                <div className="grid lg:grid-cols-[minmax(0,1.45fr)_minmax(280px,0.55fr)]">
                  <div className="p-5">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Priority queue</p>
                        <p className="mt-1 text-sm text-slate-600">Start with the strongest combined risk signals.</p>
                      </div>
                      <span className="border border-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-600">
                        {riskHighlights.length} students
                      </span>
                    </div>

                    <div className="mt-5 space-y-3">
                      {riskHighlights.map((item) => {
                        const riskClass =
                          item.riskLevel === "High"
                            ? "border-l-rose-500 bg-rose-50/60"
                            : item.riskLevel === "Medium"
                              ? "border-l-amber-500 bg-amber-50/60"
                              : "border-l-emerald-500 bg-emerald-50/60";
                        return (
                          <div key={item.student.id} className={`border border-slate-200 border-l-4 p-4 ${riskClass}`}>
                            <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
                              <div className="min-w-[190px]">
                                <div className="flex items-center gap-3">
                                  <div className="flex h-10 w-10 items-center justify-center border border-slate-300 bg-white text-sm font-semibold text-slate-700">
                                    {item.student.firstName?.[0] || <FaUserCircle />}
                                  </div>
                                  <div>
                                    <p className="font-semibold text-slate-900">{item.student.firstName} {item.student.lastName}</p>
                                    <p className="text-xs text-slate-500">{item.student.rollNumber || item.student.email}</p>
                                  </div>
                                </div>
                              </div>

                              <div className="grid flex-1 gap-3 sm:grid-cols-2">
                                <div>
                                  <div className="mb-1 flex justify-between text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                    <span>Attendance</span><span>{item.attendancePercent}%</span>
                                  </div>
                                  <div className="h-2 bg-slate-200"><div className="h-full bg-cyan-500" style={{ width: `${item.attendancePercent}%` }} /></div>
                                </div>
                                <div>
                                  <div className="mb-1 flex justify-between text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                    <span>Marks</span><span>{item.marksPercent}%</span>
                                  </div>
                                  <div className="h-2 bg-slate-200"><div className="h-full bg-violet-500" style={{ width: `${item.marksPercent}%` }} /></div>
                                </div>
                              </div>

                              <div className="min-w-[170px] text-left xl:text-right">
                                <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Risk score</p>
                                <p className="mt-1 text-2xl font-semibold text-slate-900">{item.riskScore}<span className="text-sm text-slate-400">/100</span></p>
                                <p className="mt-1 text-xs font-medium text-slate-600">{item.action}</p>
                              </div>
                            </div>
                            <div className="mt-3 flex flex-wrap gap-2">
                              {item.reasons.map((reason) => <span key={reason} className="border border-slate-200 bg-white px-2 py-1 text-[11px] font-medium text-slate-600">{reason}</span>)}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <aside className="border-t border-slate-200 bg-slate-50 p-5 lg:border-l lg:border-t-0">
                    <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Recommended focus</p>
                    <div className="mt-4 border border-slate-200 bg-white p-4">
                      <p className="text-sm font-semibold text-slate-900">{riskCounts.high ? "Contact priority families" : "Keep momentum moving"}</p>
                      <p className="mt-2 text-sm leading-6 text-slate-600">
                        {riskCounts.high
                          ? `${riskCounts.high} student${riskCounts.high === 1 ? "" : "s"} need a direct follow-up this week.`
                          : "No high-risk students in the current view. Share recognition with the strongest improvers."}
                      </p>
                    </div>
                    <div className="mt-4 space-y-3 text-sm">
                      <div className="flex items-center justify-between border-b border-slate-200 pb-3"><span className="text-slate-600">Marks movement</span><span className={`inline-flex items-center gap-1 font-semibold ${averageInsightTrend >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{averageInsightTrend >= 0 ? <FaArrowUp /> : <FaArrowDown />}{Math.abs(averageInsightTrend)} pts</span></div>
                      <div className="flex items-center justify-between border-b border-slate-200 pb-3"><span className="text-slate-600">Stable students</span><span className="font-semibold text-emerald-700">{riskCounts.low}</span></div>
                      <div className="flex items-center justify-between"><span className="text-slate-600">Suggested action</span><span className="font-semibold text-slate-900">Support huddle</span></div>
                    </div>
                  </aside>
                </div>
              </div>
            )}

            {selectedInsightsTab === "performance" && showPerformanceInsights && (
              <div>
                <div className="flex flex-col gap-4 border-b border-slate-200 bg-cyan-50 px-5 py-5 lg:flex-row lg:items-center lg:justify-between">
                  <div>
                    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-cyan-700"><FaChartLine /> Learning pulse</div>
                    <h2 className="mt-2 text-2xl font-semibold text-slate-900">Performance Momentum</h2>
                    <p className="mt-1 text-sm text-slate-600">Compare subject strength, student movement, and the next support opportunity.</p>
                  </div>
                  <span className="w-fit border border-cyan-200 bg-white px-3 py-1.5 text-xs font-semibold text-cyan-800">{usingSampleInsightData ? "Sample signal data" : "Live class signals"}</span>
                </div>

                <div className="grid divide-y divide-slate-200 border-b border-slate-200 sm:grid-cols-3 sm:divide-x sm:divide-y-0">
                  <div className="px-5 py-4"><p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Class average</p><p className="mt-1 text-3xl font-semibold text-slate-900">{classAverage}%</p></div>
                  <div className="px-5 py-4"><p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Momentum</p><p className={`mt-1 flex items-center gap-2 text-3xl font-semibold ${averageInsightTrend >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{averageInsightTrend >= 0 ? <FaArrowUp className="text-lg" /> : <FaArrowDown className="text-lg" />}{Math.abs(averageInsightTrend)}<span className="text-sm font-medium">pts</span></p></div>
                  <div className="px-5 py-4"><p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Tracking</p><p className="mt-1 text-3xl font-semibold text-slate-900">{performanceRows.length}</p></div>
                </div>

                <div className="grid lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)]">
                  <div className="p-5">
                    <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Subject signal</p>
                    <div className="mt-4 space-y-5">
                      {subjectAverages.map((item, index) => {
                        const colors = ["bg-cyan-500", "bg-violet-500", "bg-amber-500"];
                        return (
                          <div key={item.subject}>
                            <div className="flex items-center justify-between text-sm"><span className="font-semibold text-slate-800">{item.subject}</span><span className="font-semibold text-slate-900">{item.average}%</span></div>
                            <div className="mt-2 h-3 bg-slate-100"><div className={`h-full ${colors[index]}`} style={{ width: `${item.average}%` }} /></div>
                          </div>
                        );
                      })}
                    </div>

                    <div className="mt-8 border-t border-slate-200 pt-5">
                      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Student movement</p>
                      <div className="mt-3 divide-y divide-slate-100 border-y border-slate-200">
                        {performanceRows.map((row) => (
                          <div key={row.student.id} className="flex items-center justify-between gap-3 py-3">
                            <div><p className="text-sm font-semibold text-slate-800">{row.student.firstName} {row.student.lastName}</p><p className="text-xs text-slate-500">{row.student.rollNumber || row.student.email}</p></div>
                            <div className="flex items-center gap-4"><span className="text-sm font-semibold text-slate-900">{row.percentage}%</span><span className={`inline-flex min-w-[62px] items-center justify-end gap-1 text-xs font-semibold ${row.trendDelta >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{row.trendDelta >= 0 ? <FaArrowUp /> : <FaArrowDown />}{Math.abs(row.trendDelta)} pts</span></div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <aside className="border-t border-slate-200 bg-slate-50 p-5 lg:border-l lg:border-t-0">
                    <div className="border border-emerald-200 bg-emerald-50 p-4">
                      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-emerald-700"><FaStar /> Top performers</div>
                      <div className="mt-4 space-y-3">
                        {topPerformers.map((row) => <div key={row.student.id} className="flex items-center justify-between"><div><p className="text-sm font-semibold text-slate-800">{row.student.firstName} {row.student.lastName}</p><p className="text-xs text-slate-500">{row.student.rollNumber || row.student.email}</p></div><span className="text-sm font-semibold text-emerald-700">{row.percentage}%</span></div>)}
                      </div>
                    </div>
                    <div className="mt-4 border border-rose-200 bg-rose-50 p-4">
                      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-rose-700"><FaExclamationTriangle /> Support queue</div>
                      <div className="mt-4 space-y-3">
                        {needsHelp.map((row) => <div key={row.student.id} className="flex items-center justify-between"><div><p className="text-sm font-semibold text-slate-800">{row.student.firstName} {row.student.lastName}</p><p className="text-xs text-slate-500">{row.student.rollNumber || row.student.email}</p></div><span className="text-sm font-semibold text-rose-700">{row.percentage}%</span></div>)}
                      </div>
                    </div>
                  </aside>
                </div>
              </div>
            )}
          </div>
        )}

        {activeSection === "account" && (
          <div className="space-y-4">
            <div className="grid gap-4 grid-cols-2 xl:grid-cols-4">
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Payments recorded</p>
                <p className="mt-2 text-2xl font-semibold text-slate-900">{accountSummary.paymentsCount}</p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Paid this month</p>
                <p className="mt-2 text-2xl font-semibold text-slate-900">₹{accountSummary.paidThisMonth.toLocaleString("en-IN")}</p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Total paid</p>
                <p className="mt-2 text-2xl font-semibold text-slate-900">₹{accountSummary.totalPaid.toLocaleString("en-IN")}</p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Last payment</p>
                <p className="mt-2 text-2xl font-semibold text-slate-900">{latestTeacherPayment?.paidOn || "-"}</p>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">Account details</h3>
                  <p className="text-sm text-slate-500">This section shows salary payment details processed by admin.</p>
                </div>
                <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">Teacher view</span>
              </div>

              <div className="mt-4 grid gap-3 md:grid-cols-2">
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Account holder</p>
                  <p className="mt-1 text-sm font-semibold text-slate-800">{latestTeacherPayment?.accountHolderName || loggedInTeacherName || "-"}</p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Bank / UPI</p>
                  <p className="mt-1 text-sm font-semibold text-slate-800">{latestTeacherPayment?.bankName || latestTeacherPayment?.upiId || "-"}</p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Account number</p>
                  <p className="mt-1 text-sm font-semibold text-slate-800">{maskAccount(latestTeacherPayment?.accountNumber)}</p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">IFSC / UPI ID</p>
                  <p className="mt-1 text-sm font-semibold text-slate-800">{latestTeacherPayment?.ifscCode || latestTeacherPayment?.upiId || "-"}</p>
                </div>
              </div>

              <div className="mt-4 overflow-hidden rounded-xl border border-slate-200">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[680px] text-left text-sm">
                    <thead className="bg-slate-100 text-slate-600">
                      <tr>
                        <th className="px-4 py-3 font-semibold">Paid on</th>
                        <th className="px-4 py-3 font-semibold">Amount</th>
                        <th className="px-4 py-3 font-semibold">Method</th>
                        <th className="px-4 py-3 font-semibold">Reference</th>
                        <th className="px-4 py-3 font-semibold">Processed by</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white text-slate-800">
                      {teacherPayments.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="px-4 py-8 text-center text-sm text-slate-500">
                            No payment records available yet.
                          </td>
                        </tr>
                      ) : teacherPayments.map((payment) => (
                        <tr key={payment.id} className="border-t border-slate-200">
                          <td className="px-4 py-3">{payment.paidOn || "-"}</td>
                          <td className="px-4 py-3 font-semibold">₹{Number(payment.amount || 0).toLocaleString("en-IN")}</td>
                          <td className="px-4 py-3">{String(payment.method || "-").replaceAll("_", " ")}</td>
                          <td className="px-4 py-3">{payment.referenceId || "-"}</td>
                          <td className="px-4 py-3">{payment.processedBy || "Admin"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeSection === "students" && studentsEnabled && (
          <div className="space-y-5">
            {renderTopTabs(studentsTabs, selectedStudentsTab, setActiveStudentsTab)}

            {selectedStudentsTab === "reports" && showStudentReports && (
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-center gap-2 text-slate-700 font-semibold">
                    <FaFileAlt />
                    Student Reports
                  </div>
                  <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                    {reportStudents.length} students
                  </span>
                </div>
                <div className="mt-4 space-y-2 text-sm">
                  {reportStudents.length === 0 && (
                    <div className="rounded-xl border border-dashed border-slate-200 px-4 py-6 text-slate-500">
                      No students available for reports.
                    </div>
                  )}
                  {reportStudents.slice(0, 8).map((s) => (
                    <div key={s.id} className="flex flex-col gap-3 rounded-xl border border-slate-100 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <div className="font-medium text-slate-800">{s.firstName} {s.lastName}</div>
                        <div className="text-xs text-slate-500">{s.rollNumber || s.email}</div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <button type="button" onClick={() => openReportCard(s)} className="rounded-md border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50">View Report</button>
                        <button type="button" onClick={() => openProfile(s)} className="rounded-md border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50">Profile</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selectedStudentsTab === "marks" && showSubmitMarks && (
              <div className="overflow-hidden rounded-3xl border border-indigo-100 bg-white shadow-sm">
                <div className="border-b border-indigo-100 bg-[linear-gradient(120deg,#eef2ff_0%,#f8fafc_55%,#ecfeff_100%)] px-5 py-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <div className="flex items-center gap-2 text-base font-semibold text-slate-900">
                        <FaGraduationCap className="text-indigo-600" />
                        Submit Marks
                      </div>
                      <p className="mt-1 text-xs font-medium text-slate-500">Record marks for {selectedClassName}.</p>
                    </div>
                    <span className="rounded-full border border-indigo-200 bg-white/80 px-3 py-1 text-xs font-semibold text-indigo-700">
                      Max {markForm.max || 100}
                    </span>
                  </div>
                </div>

                <form onSubmit={handleMarkSubmit} className="grid gap-5 p-5 lg:grid-cols-[minmax(0,1.25fr)_minmax(260px,0.75fr)]">
                  <div className="space-y-4">
                    <label className="block">
                      <span className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Student</span>
                      <select
                        value={markForm.studentId}
                        onChange={(e) => setMarkForm((prev) => ({ ...prev, studentId: e.target.value }))}
                        className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm font-medium text-slate-800 outline-none transition focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
                      >
                        <option value="">Select Student</option>
                        {reportStudents.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.firstName} {s.lastName}
                          </option>
                        ))}
                      </select>
                    </label>

                    <div className="grid gap-3 sm:grid-cols-3">
                      <label className="block sm:col-span-1">
                        <span className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Subject</span>
                        <input
                          value={markForm.subject}
                          onChange={(e) => setMarkForm((prev) => ({ ...prev, subject: e.target.value }))}
                          className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm font-medium text-slate-800 outline-none transition focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
                          placeholder="Subject"
                        />
                      </label>
                      <label className="block">
                        <span className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Score</span>
                        <input
                          type="number"
                          min="0"
                          value={markForm.score}
                          onChange={(e) => setMarkForm((prev) => ({ ...prev, score: e.target.value }))}
                          className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm font-medium text-slate-800 outline-none transition focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
                          placeholder="Score"
                        />
                      </label>
                      <label className="block">
                        <span className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Max</span>
                        <input
                          type="number"
                          min="1"
                          value={markForm.max}
                          onChange={(e) => setMarkForm((prev) => ({ ...prev, max: e.target.value }))}
                          className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm font-medium text-slate-800 outline-none transition focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
                          placeholder="Max"
                        />
                      </label>
                    </div>

                    <label className="block">
                      <span className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Remarks</span>
                      <textarea
                        value={markForm.remarks}
                        onChange={(e) => setMarkForm((prev) => ({ ...prev, remarks: e.target.value }))}
                        className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm text-slate-800 outline-none transition focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
                        rows="3"
                        placeholder="Remarks (optional)"
                      />
                    </label>
                  </div>

                  <div className="flex flex-col justify-between rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Preview</p>
                      <div className="mt-4">
                        <p className="text-sm font-semibold text-slate-900">
                          {selectedMarkStudent
                            ? `${selectedMarkStudent.firstName} ${selectedMarkStudent.lastName}`
                            : "No student selected"}
                        </p>
                        <p className="mt-1 text-xs text-slate-500">{markForm.subject || "Subject"}</p>
                      </div>
                      <div className="mt-5 flex items-end gap-2">
                        <span className="text-4xl font-semibold text-slate-900">{markForm.score || "0"}</span>
                        <span className="pb-1 text-sm font-semibold text-slate-500">/ {markForm.max || 100}</span>
                      </div>
                    </div>
                    <button type="submit" className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white shadow-lg shadow-slate-300 transition hover:-translate-y-0.5 hover:bg-indigo-700">
                      <FaCheckCircle />
                      Submit Marks
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        )}

        {activeSection === "resources" && resourcesEnabled && (
          <div className="space-y-5">
            {renderTopTabs(resourcesTabs, selectedResourcesTab, setActiveResourcesTab)}

            {selectedResourcesTab === "notes" && showUploadNotes && (
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-center gap-2 text-slate-700 font-semibold">
                  <FaUpload />
                  Upload Class Notes
                </div>
                <form onSubmit={handleNoteSubmit} className="mt-3 space-y-3 text-sm">
                  <input
                    value={noteForm.title}
                    onChange={(e) => setNoteForm((prev) => ({ ...prev, title: e.target.value }))}
                    className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                    placeholder="Notes title"
                  />
                  <select
                    value={noteForm.classId}
                    onChange={(e) => setNoteForm((prev) => ({ ...prev, classId: e.target.value }))}
                    className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                  >
                    <option value="">Select Class</option>
                    {classOptions.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                  <input
                    type="file"
                    onChange={(e) => setNoteForm((prev) => ({ ...prev, file: e.target.files?.[0] || null }))}
                    className="w-full text-sm text-slate-600"
                  />
                  <button type="submit" className="w-full rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-700">
                    Upload Notes
                  </button>
                </form>
                <div className="mt-4 space-y-2 text-sm">
                  {classNotes.map((note) => (
                    <div key={note.id} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2">
                      <div>
                        <div className="font-medium text-slate-800">{note.title}</div>
                        <div className="text-xs text-slate-500">{note.className} • {note.date}</div>
                      </div>
                      <button className="text-xs px-2 py-1 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50">
                        Download
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selectedResourcesTab === "leave" && showLeaveRequests && (
                  <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                    <div className="flex items-center gap-2 text-slate-700 font-semibold">
                      <FaClipboardList />
                      Leave Requests
                    </div>
                    <div className="mt-3 space-y-2 text-sm">
                      {leaveRequests.length === 0 && (
                        <div className="text-slate-400">No pending requests</div>
                      )}
                      {leaveRequests.map((req) => (
                        <div key={req.id} className="rounded-lg border border-slate-100 px-3 py-2">
                          <div className="font-medium text-slate-800">{req.studentName}</div>
                          <div className="text-xs text-slate-500">{req.from} to {req.to} • {req.reason}</div>
                          <div className="mt-2 flex gap-2">
                            <button
                              onClick={() => handleLeaveDecision(req.id, "approved")}
                              className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-emerald-100 text-emerald-700"
                            >
                              <FaCheckCircle />
                              Approve
                            </button>
                            <button
                              onClick={() => handleLeaveDecision(req.id, "rejected")}
                              className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-rose-100 text-rose-700"
                            >
                              <FaTimesCircle />
                              Reject
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

            {selectedResourcesTab === "holiday" && showHolidayRequests && (
                  <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                    <div className="flex items-center gap-2 text-slate-700 font-semibold">
                      <FaClipboardList />
                      Holiday Requests
                    </div>
                    <div className="mt-3 space-y-2 text-sm">
                      {holidayRequests.length === 0 && (
                        <div className="text-slate-400">No pending requests</div>
                      )}
                      {holidayRequests.map((req) => (
                        <div key={req.id} className="rounded-lg border border-slate-100 px-3 py-2">
                          <div className="font-medium text-slate-800">{req.title}</div>
                          <div className="text-xs text-slate-500">{req.date} • {req.requestedBy}</div>
                          <div className="text-xs text-slate-500">{req.reason}</div>
                          <div className="mt-2 flex gap-2">
                            <button
                              onClick={() => handleHolidayDecision(req.id, "approved")}
                              className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-emerald-100 text-emerald-700"
                            >
                              <FaCheckCircle />
                              Approve
                            </button>
                            <button
                              onClick={() => handleHolidayDecision(req.id, "rejected")}
                              className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-rose-100 text-rose-700"
                            >
                              <FaTimesCircle />
                              Reject
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
            )}
          </div>
        )}

        {activeSection === "assessments" && showAssessments && (
          <div className="rounded-3xl border border-indigo-100 bg-white p-6 shadow-sm">
            <ProctoredAssessment
              creatorRole="teacher"
              creatorName={displayName}
            />
          </div>
        )}

        {activeSection === "usage" && (
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Student Usage Monitor</h3>
                <p className="text-sm text-slate-600">View active, inactive, and no-usage students with time spent trends.</p>
              </div>
              <Link
                to="/teacher/students-activity"
                className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800"
              >
                Open Activity Dashboard
              </Link>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {showProfile && activeStudent && (
        <StudentProfileModal
          student={activeStudent}
          onClose={() => setShowProfile(false)}
          onSave={saveStudentUpdate}
        />
      )}

      {showReportCard && activeStudent && (
        <ReportCardModal
          student={activeStudent}
          onClose={() => setShowReportCard(false)}
          onSave={(data) => saveReportCard(activeStudent.id, data)}
        />
      )}

      <AdminModalShell isOpen={correctionModal.open} onClose={closeCorrectionModal} maxWidth="max-w-lg">
        <div className="p-6">
          <h3 className="text-lg font-bold text-slate-900">Request Attendance Correction</h3>
          <p className="mt-1 text-sm text-slate-500">
            Explain why this attendance record needs to be corrected. An admin must approve your request before it can be applied.
          </p>
          <label className="mt-4 block text-sm font-semibold text-slate-700">
            Reason
            <textarea
              value={correctionModal.reason}
              onChange={(event) =>
                setCorrectionModal((prev) => ({ ...prev, reason: event.target.value }))
              }
              rows={4}
              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-blue-400 focus:outline-none"
              placeholder="e.g. Student was marked absent by mistake"
            />
          </label>
          <div className="mt-5 flex justify-end gap-3">
            <button
              type="button"
              onClick={closeCorrectionModal}
              disabled={submittingCorrection}
              className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submitCorrectionRequest}
              disabled={submittingCorrection}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submittingCorrection ? "Submitting..." : "Submit Request"}
            </button>
          </div>
        </div>
      </AdminModalShell>
    </div>
  );
}
